import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useAuth } from '../auth/AuthContext';
import { canCreateBookingsByLoginType, isBookingRoleDeniedApiMessage } from '../auth/sessionBookingRole';
import { isAssetOwnerLoginType, isBusinessWorkerLogin } from '../auth/userLoginType';
import { resolveApiPath } from '../baseUrl';
import { AssetRequestError, shortAssetRequestErrorSummary } from '../api/getAssetById';
import {
  cancelBookingAsOwner,
  createBooking,
  escalateBooking,
  fetchBookingById,
  fetchBookingsList,
  modifyBookingPrice,
  updateBooking,
} from '../api/bookingApi';
import {
  cancelBookingAsClientResilient,
  filterDismissedBookingsFromList,
} from '../utils/bookingCancelResilience';
import { scheduleScrollPanelToElement, scheduleScrollPanelToTop } from '../utils/panelFocusScroll';
import {
  bookingCreateBodyWithoutAddons,
  normalizeBookingCreatePayload,
} from '../api/bookingCreatePayload';
import {
  fetchAssetAddons,
  fetchAssetPricingTypes,
  getAssetById,
} from '../api/getAssetById';
import {
  BOOKING_CREATE_ADDON_OPTIONS,
  defaultBookingCreateBody,
  emptyBookingCreateBody,
} from '../api/bookingDefaultBodies';
import { useBookingCreate, type QuickBookingDraft, type QuickBookingMode } from '../booking/BookingCreateContext';
import { useProfileBookingGate } from '../booking/ProfileBookingGateContext';
import { useLastAppError } from '../errors/LastAppErrorContext';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { AssetQuickBookingPanel } from './AssetQuickBookingPanel';
import { floorAssistantTimeToHour } from '../assistant/parseAssistantWhen';
import { BookingResponseView } from './BookingResponseView';
import { normalizeBookingListRecords } from '../utils/bookingListNormalize';
import {
  AssetGetByIdView,
  assetRecordDisplayTitle,
  assetRecordListAddressLine,
  canRenderAssetGetByIdView,
} from './AssetGetByIdView';
import {
  bookingRecordAssetId,
  bookingRecordAssetName,
  bookingRecordDateRangeLabel,
  bookingRecordIsHourly,
  bookingRecordNestedAsset,
  bookingRecordScheduleLabel,
} from '../utils/bookingListDisplay';
import { rememberBrowseAssetListSnapshot } from '../utils/browseAssetListSnapshot';
import { enrichAssetRecordAddressForDisplay } from '../utils/assetAddressPostalCode';
import { mergeAssetApiRecordWithListSnapshot } from '../utils/assetRecordMerge';
import { prepareFavoriteAssetSnapshot } from '../favorites/favoriteAssetsStorage';
import { BookingConfirmationCard } from './BookingConfirmationCard';
import { BookingPaymentPanel } from './BookingPaymentPanel';
import { BookingPaymentReturnPanel } from './BookingPaymentReturnPanel';
import { BookingAwaitingPaymentPanel } from './BookingAwaitingPaymentPanel';
import { BookingWorkerCreditsPaymentPanel } from './BookingWorkerCreditsPaymentPanel';
import { BookingCancellationPolicyPanel } from './BookingCancellationPolicyPanel';
import { GymHealthDeclarationPanel } from './GymHealthDeclarationPanel';
import { BookingParkingVehiclePanel } from './BookingParkingVehiclePanel';
import {
  fetchMedicalQuestionnaireCompleted,
  markMedicalQuestionnaireCompleted,
} from '../api/medicalQuestionnaireProfile';
import { assetIsGymBooking } from '../utils/assetIsGymBooking';
import { assetIsParkingBooking } from '../utils/assetIsParkingBooking';
import type { GymHealthDeclarationWirePayload } from '../types/gymHealthDeclaration';
import {
  formatProfileVehicleSummaryLine,
  type ProfileVehicle,
} from '../utils/profileVehicles';
import {
  resolveBookingConfirmationVenue,
  type BookingConfirmationDetails,
} from '../utils/bookingConfirmationWindow';
import {
  bookingRecordCanMutate,
  bookingRecordPrimaryId,
  extractBookingIdFromApiPayload,
  recoverCreatedBookingId,
  bookingUpdateBodyFromRecord,
  filterActiveMyBookingsListRecords,
  normalizeBookingUpdatePayload,
  type BookingListTimeFilter,
} from '../utils/bookingRecordMutate';
import {
  bookingRecordIsPaymentConfirmed,
  bookingRecordNeedsPayment,
  clearPayplusPendingCheckout,
  openPayplusPaymentPage,
  plantPayplusBrowserBackToAsset,
  readPayplusPendingCheckout,
  startPayplusCheckoutForBooking,
  unwrapBookingRecord,
} from '../utils/payplusPayment';
import { PAYPLUS_TEST_CHARGE_NIS } from '../utils/bookingPaymentValidate';
import { deepClone, setAtPath } from '../utils/cloneAndPath';
import type { BookingPaymentPayOptions } from './BookingPaymentPanel';
import {
  assetSupportsHourlyBooking,
  normalizePricingTypesCatalog,
  resolveAssetBookingPricingOptions,
  type AssetBookingPricingOption,
} from '../utils/assetBookingPricingOptions';
import { resolveHourDayBookingPricingOptions } from '../utils/assetBookingPrices';
import {
  addonOptionDisplayLabel,
  applyBookingEditAddonAvailability,
  buildBookingAddonsWirePayload,
  extractBookingAddonQuantitiesFromBody,
  formatBookingAddonAvailabilityLabel,
  clampBookingAddonQuantity,
  humanizeBookingAddonErrorMessage,
  normalizeAddonsCatalog,
  resolveAssetBookingAddonOptions,
  validateBookingAddonQuantities,
  validateBookingAddonsInCatalog,
  type AssetBookingAddonOption,
} from '../utils/bookingAssetAddons';
import {
  addBookingPeriods,
  formatBookingInstantPreview,
} from '../utils/bookingPeriodEndDate';
import {
  formatBookingPeriodCountLabel,
  formatBookingPeriodStartPlus,
} from '../i18n/bookingFormLabels';
import {
  coercePricingTypeIdFromRecord,
  computeBookingWireTotalPrice,
  countBookingPeriodsBetween,
  resolveBookingPricingOptionForUpdate,
} from '../utils/bookingTotalPriceCompute';
import { bookingConfirmationShowsPeopleCount } from '../utils/assetBookingPeopleCount';
import {
  isAssetNotBookableApiMessage,
  resolveAssetForBookingCreate,
  mergeBookingAssetSnapshot,
  assetRecordIsBookable,
} from '../utils/assetBookability';
import { assetRecordMutationId } from '../utils/assetRecordPrimaryId';
import { saveBookingPricingTypePreference } from '../utils/bookingPricingTypeStorage';
import { localizeBookingScheduleError } from '../utils/bookingScheduleErrorLabels';
import {
  isIncompleteRegistrationApiMessage,
  isIncompleteRegistrationBookingError,
} from '../utils/bookingProfileGateAuthError';
import { localizeBookingPeopleCapacityError } from '../utils/bookingPeopleCapacityValidate';
import { validateBookingCreateOrderReady } from '../utils/bookingCreateOrderValidate';
import { refreshStaleQuickBookingSchedule } from '../utils/bookingQuickScheduleIso';
import { applyBookingVat, resolveBookingCheckoutSubtotal } from '../utils/bookingVat';
import { normalizeWorkerCreditsPaymentWire } from '../utils/businessWorkerCreditPayment';
import {
  normalizePrivateClientPayWithCreditsWire,
  resolvePrivateClientCreditsFromProfilePayload,
} from '../utils/privateClientCredits';
import { getStoredProfileWalletCredits } from '../utils/profileWalletCreditsStorage';
import { fetchProfile } from '../api/profileApi';
import { getStoredLoginIdentifier } from '../auth/sessionToken';
import {
  BOOKING_DAILY_PRICING_TYPE_ID,
  BOOKING_HOURLY_PRICING_TYPE_ID,
  BOOKING_MONTHLY_PRICING_TYPE_ID,
} from '../constants/bookingQuickDefaults';
import {
  bookingEndIsoForPeriodCount,
  clampHourlyStartTimeOnToday,
  maxHourlyPeriodCountSameDay,
  resolveBookingEndDefaultTime,
  resolveBookingPeriodSchedule,
  resolveBookingStartDefaultTime,
  validateBookingUpdateEndDate,
  validateBookingUpdatePeriodSchedule,
  type ScheduleDisplay,
} from '../utils/bookingScheduleDefaults';
import { localizeCatalogDisplayName, useI18n, type MessageKey } from '../i18n';
import { LocaleTimeInput } from './LocaleTimeInput';
import {
  DEFAULT_ASSET_LOCALE_CURRENCY,
  formatMoneyAmount,
  resolveAssetLocaleCurrency,
  type AssetLocaleCurrency,
} from '../utils/assetCountryCurrency';
import type { TranslateParams } from '../i18n/translate';
import { bookingOperations } from '../i18n/operationLabels';
import {
  calendarDayKeyToScheduleDisplay,
  collectReservedDayKeysForAsset,
  isoToCalendarDayKey,
  normalizeCalendarRangeKeys,
  rangeIncludesReservedDay,
} from '../utils/assetBookingCalendar';
import { calendarDayIsClosed } from '../utils/openingHoursBookingValidate';
import { AssetBookingCalendar } from './AssetBookingCalendar';
import styles from './BookingMenu.module.css';
import pageThemeStyles from './menuPageTheme.module.css';

type Operation =
  | 'getById'
  | 'listAll'
  | 'create'
  | 'modifyPrice'
  | 'cancelClient'
  | 'cancelOwner'
  | 'escalate';

type CreateFlowOrigin = 'assets' | 'bookings';

const DEMO_BOOKING_ID = '9d6da942-b6fd-4a46-a210-82e357dcef15';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function operationNeedsBookingId(op: Operation): boolean {
  return op !== 'create' && op !== 'listAll';
}

function operationUsesDangerSubmit(op: Operation): boolean {
  return op === 'cancelClient' || op === 'cancelOwner';
}

function operationNeedsCreateBody(op: Operation): boolean {
  return op === 'create';
}

function submitLabel(
  t: (key: MessageKey, params?: TranslateParams) => string,
  op: Operation,
  loading: boolean,
  editingBookingId?: string | null,
): string {
  if (loading) {
    return '…';
  }
  switch (op) {
    case 'getById':
      return t('common.fetch');
    case 'listAll':
      return t('common.fetch');
    case 'create':
      return editingBookingId ? t('common.update') : t('common.create');
    case 'modifyPrice':
      return t('common.submit');
    case 'cancelClient':
    case 'cancelOwner':
      return t('common.cancel');
    case 'escalate':
      return t('bookings.operation.escalate');
    default: {
      const _ex: never = op;
      return _ex;
    }
  }
}

type BookingFailureContext = {
  operation: Operation;
  bookingId: string;
  /** When set, create flow submits PATCH `/bookings/:id` instead of POST create. */
  editingBookingId?: string | null;
  modifyPriceInput?: string;
  ownerCancelAnswer?: boolean;
};

function reportBookingFailureToApp(
  reportError: ReturnType<typeof useLastAppError>['reportError'],
  ctx: BookingFailureContext,
  err: unknown,
  extraContext?: Record<string, unknown>,
): void {
  const message =
    err instanceof AssetRequestError
      ? err.message
      : err instanceof Error
        ? err.message
        : 'Something went wrong.';
  reportError({
    source: `bookings.${ctx.operation}`,
    message,
    request: formatBookingAttempt(ctx),
    context: extraContext ? { ...ctx, ...extraContext } : ctx,
    assetError: err instanceof AssetRequestError ? err : undefined,
  });
}

function formatBookingAttempt(ctx: BookingFailureContext): string {
  const idLabel = ctx.bookingId || '(empty — set Booking ID)';
  switch (ctx.operation) {
    case 'getById':
      return `GET ${resolveApiPath(`bookings/${encodeURIComponent(idLabel)}`)}`;
    case 'listAll':
      return `GET ${resolveApiPath('bookings/')}`;
    case 'create': {
      const updateId = ctx.editingBookingId?.trim();
      if (updateId) {
        return `PATCH ${resolveApiPath(`bookings/${encodeURIComponent(updateId)}`)} (body: end_date, total_price, addons)`;
      }
      return `POST ${resolveApiPath('bookings/create')} (body: asset_id, dates, people, etc.)`;
    }
    case 'modifyPrice':
      return `POST ${resolveApiPath('bookings/modifyprice')} — booking_id: ${idLabel}, total_price: ${ctx.modifyPriceInput ?? '(see form)'}`;
    case 'cancelClient':
      return `PATCH ${resolveApiPath(`bookings/cancel/client/${encodeURIComponent(idLabel)}`)}`;
    case 'cancelOwner': {
      const ans = ctx.ownerCancelAnswer === true ? 'true' : 'false';
      return `PATCH ${resolveApiPath(`bookings/cancel/owner/${encodeURIComponent(idLabel)}`)} with body { answer: ${ans} }`;
    }
    case 'escalate':
      return `POST ${resolveApiPath(`bookings/escalate/${encodeURIComponent(idLabel)}`)}`;
    default: {
      const _x: never = ctx.operation;
      return String(_x);
    }
  }
}

function dedupeHints(hints: string[]): string[] {
  return hints.filter((h, i) => hints.indexOf(h) === i);
}

const BOOKING_MONTH_OPTIONS: { value: string; label: string }[] = Array.from({ length: 12 }, (_, m) => ({
  value: String(m),
  label: new Intl.DateTimeFormat(undefined, { month: 'long' }).format(new Date(2024, m, 1)),
}));

type YmdTime = { y: number; m: number; d: number; time: string };

function isoToYmdTime(iso: unknown): YmdTime | null {
  if (iso == null || typeof iso !== 'string') {
    return null;
  }
  const s = iso.trim();
  if (!s) {
    return null;
  }
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) {
    return null;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    y: dt.getFullYear(),
    m: dt.getMonth(),
    d: dt.getDate(),
    time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
  };
}

function clampDayInMonth(year: number, monthIndex: number, day: number): number {
  const max = new Date(year, monthIndex + 1, 0).getDate();
  return Math.min(Math.max(1, day), max);
}

function ymdTimeToIso(v: YmdTime): string {
  const [hh, mm] = v.time.split(':').map((x) => parseInt(x, 10));
  const h = Number.isFinite(hh) ? hh : 0;
  const min = Number.isFinite(mm) ? mm : 0;
  const dc = clampDayInMonth(v.y, v.m, v.d);
  const d = new Date(v.y, v.m, dc, h, min, 0, 0);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return d.toISOString();
}

function isoToDisplay(iso: unknown): ScheduleDisplay {
  const p = isoToYmdTime(iso);
  if (!p) {
    return { y: '', m: '', d: '', t: '' };
  }
  return { y: String(p.y), m: String(p.m), d: String(p.d), t: p.time };
}

function displayToIso(d: ScheduleDisplay): string {
  const y = d.y.trim();
  const m = d.m.trim();
  const day = d.d.trim();
  const t = d.t.trim();
  if (y === '' || m === '' || day === '' || t === '') {
    return '';
  }
  const yn = parseInt(y, 10);
  const mn = parseInt(m, 10);
  const dn = parseInt(day, 10);
  if (!Number.isFinite(yn) || !Number.isFinite(mn) || !Number.isFinite(dn)) {
    return '';
  }
  if (!/^\d{2}:\d{2}$/.test(t)) {
    return '';
  }
  return ymdTimeToIso({ y: yn, m: mn, d: dn, time: t });
}

function bookingAddonQuantitiesInBody(addons: unknown): Map<number, number> {
  return extractBookingAddonQuantitiesFromBody(addons);
}

function bookingSelectedAddonIds(addons: unknown): Set<number> {
  return new Set(extractBookingAddonQuantitiesFromBody(addons).keys());
}

function bookingAddonsBodyFromSelection(
  selectedIds: Set<number>,
  quantities: Map<number, number>,
): Array<{ addon_id: number; quantity: number }> {
  return Array.from(selectedIds).map((addon_id) => ({
    addon_id,
    quantity: quantities.get(addon_id) ?? 1,
  }));
}

function displayToIsoWithDefaults(
  d: ScheduleDisplay,
  defaultTime: string,
  opts?: { clampHourlyStartOnToday?: boolean },
): string {
  const y = d.y.trim();
  const m = d.m.trim();
  const day = d.d.trim();
  if (y === '' || m === '' || day === '') {
    return '';
  }
  const tIn = d.t.trim();
  let t = tIn === '' ? defaultTime : tIn;
  if (opts?.clampHourlyStartOnToday) {
    t = clampHourlyStartTimeOnToday(d, t);
  }
  if (!/^\d{2}:\d{2}$/.test(t)) {
    return '';
  }
  return displayToIso({ ...d, y, m, d: day, t });
}

function useScheduleField(
  iso: unknown,
  resolveDefaultTime: (display: ScheduleDisplay) => string,
  onCommitted: (iso: string) => void,
  resetKey: string,
  opts?: { clampHourlyStartOnToday?: boolean },
) {
  const draftRef = useRef<ScheduleDisplay | null>(null);
  const [, rerender] = useState(0);

  useEffect(() => {
    draftRef.current = null;
    rerender((k) => k + 1);
  }, [resetKey]);

  useEffect(() => {
    const s = typeof iso === 'string' ? iso.trim() : '';
    if (s !== '') {
      draftRef.current = null;
      rerender((k) => k + 1);
    }
  }, [iso]);

  const display = draftRef.current ?? isoToDisplay(iso);

  const setPart = useCallback(
    (patch: Partial<ScheduleDisplay>) => {
      const base = draftRef.current ?? isoToDisplay(iso);
      const next: ScheduleDisplay = { ...base, ...patch };
      const isoOut = displayToIsoWithDefaults(next, resolveDefaultTime(next), opts);
      draftRef.current = isoOut ? null : next;
      rerender((k) => k + 1);
      onCommitted(isoOut || '');
    },
    [iso, resolveDefaultTime, onCommitted, opts],
  );

  return { display, setPart };
}

type BookingScheduleBlockProps = {
  idPrefix: string;
  legend: string;
  describedBy?: string;
  display: ScheduleDisplay;
  setPart: (patch: Partial<ScheduleDisplay>) => void;
  inputClass: string;
  monthSelectClass: string;
  controlsClass: string;
  fieldClass: string;
  fieldLabelClass: string;
  showTime?: boolean;
};

function BookingScheduleBlock({
  idPrefix,
  legend,
  describedBy,
  display,
  setPart,
  inputClass,
  monthSelectClass,
  controlsClass,
  fieldClass,
  fieldLabelClass,
  showTime = true,
}: BookingScheduleBlockProps) {
  const { t, language } = useI18n();
  return (
    <div className={fieldClass}>
      <span className={fieldLabelClass}>{legend}</span>
      <div className={controlsClass} role="group" aria-label={legend}>
        <div className={styles.scheduleDateRow}>
          <select
            id={`${idPrefix}-month`}
            className={monthSelectClass}
            value={display.m}
            onChange={(ev) => setPart({ m: ev.target.value })}
            {...(describedBy ? { 'aria-describedby': describedBy } : {})}
          >
            <option value="">{t('common.month')}</option>
            {BOOKING_MONTH_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            id={`${idPrefix}-day`}
            className={inputClass}
            type="number"
            min={1}
            max={31}
            placeholder={t('common.day')}
            value={display.d}
            onChange={(ev) => setPart({ d: ev.target.value })}
            {...(describedBy ? { 'aria-describedby': describedBy } : {})}
          />
          <input
            id={`${idPrefix}-year`}
            className={inputClass}
            type="number"
            min={1900}
            max={2100}
            placeholder={t('common.year')}
            value={display.y}
            onChange={(ev) => setPart({ y: ev.target.value })}
            {...(describedBy ? { 'aria-describedby': describedBy } : {})}
          />
        </div>
        {showTime ? (
          <div className={styles.scheduleTimeRow}>
            <LocaleTimeInput
              id={`${idPrefix}-time`}
              className={inputClass}
              selectClassName={inputClass}
              language={language}
              wholeHoursOnly
              ariaLabel={t('bookings.calendar.startTime')}
              value={display.t}
              onChange={(tValue) => setPart({ t: tValue })}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function bookingFailureHints(ctx: BookingFailureContext, err: string | AssetRequestError): string[] {
  const hints: string[] = [];

  if (typeof err === 'string') {
    hints.push('This failed before or without a full API error response — fix the issue described in the message and retry.');
    if (err.toLowerCase().includes('price')) {
      hints.push('Total price must be a finite number (you can use a comma as decimal separator in the field).');
    }
    if (err.toLowerCase().includes('booking id')) {
      hints.push('Use My Bookings to pick an id, or paste a UUID you know belongs to your account.');
    }
    return dedupeHints(hints);
  }

  const status = err.httpStatus;
  const desc = `${err.errorDescription ?? ''}\n${err.rawBody ?? ''}`.toLowerCase();

  if (status == null && err.networkDetails) {
    hints.push('No HTTP response — use “Test connection” and confirm the API or dev proxy is reachable from the browser.');
    if (err.networkDetails.includes('Failed to fetch')) {
      hints.push('Common causes: dev server not running, proxy target down, CORS, or TLS problems in direct mode.');
    }
  }

  if (status === 401) {
    hints.push(
      '401: invalid or missing Bearer token — check REACT_APP_API_TOKEN, public/token.txt (re-read each call), login session, and /auth/refresh if configured.',
    );
  }
  if (status === 403) {
    hints.push('403: authenticated but this action is not allowed for your account or booking state.');
    if (ctx.operation === 'listAll') {
      hints.push(
        'Listing all bookings (GET /bookings) is often restricted to client accounts. Try signing in as a business/private client, or ask your API team for an asset-owner bookings list route.',
      );
    }
    if (ctx.operation === 'cancelOwner') {
      hints.push('Owner cancel expects an asset-owner token; as a client, use “Cancel (client)” instead.');
    }
    if (ctx.operation === 'cancelClient') {
      hints.push('Client cancel must use the same client account that owns the booking.');
    }
    if (ctx.operation === 'modifyPrice' || ctx.operation === 'escalate') {
      hints.push('Price changes and escalation are often restricted to owners or staff — check token role.');
    }
  }
  if (status === 404) {
    if (ctx.editingBookingId?.trim()) {
      hints.push(
        '404 on PATCH /bookings/:id — booking not found or not visible to this account. Try List bookings.',
      );
    } else {
      hints.push('404: wrong or unknown booking id, or the booking is not visible to this user — try List bookings.');
    }
  }
  if (status === 400) {
    hints.push('400: invalid body or parameters — check dates, ids, and numeric fields in the form.');
  }
  if (status === 409 || status === 422) {
    hints.push('Conflict or validation — slot, rules, or booking lifecycle may reject this change.');
    if (ctx.operation === 'create') {
      hints.push(
        ctx.editingBookingId?.trim()
          ? 'Check end_date, total_price (integer ≥ 1), and add-on quantities.'
          : 'Check asset_id, start/end, amount_of_people, and addons shape.',
      );
    }
    if (ctx.operation === 'modifyPrice') {
      hints.push('The server may not allow a price change for the current booking status.');
    }
  }
  if (status != null && status >= 500) {
    hints.push('5xx: server-side failure — retry later or inspect API logs.');
  }

  if (desc.includes('ip') && (desc.includes('mismatch') || desc.includes('not allowed') || desc.includes('invalid'))) {
    hints.push('Response mentions IP restrictions — token may be bound to a public IP; compare with “Test connection” debug output when that fails.');
  }

  return dedupeHints(hints);
}

function emptyHintText(op: Operation): string {
  switch (op) {
    case 'getById':
      return '';
    case 'listAll':
      return '';
    case 'create':
      return '';
    case 'modifyPrice':
      return '';
    case 'cancelClient':
      return `PATCH ${resolveApiPath('bookings')}/cancel/client/<bookingId> as the booker.`;
    case 'cancelOwner':
      return `PATCH ${resolveApiPath('bookings')}/cancel/owner/<bookingId> with body { answer }.`;
    case 'escalate':
      return '';
    default: {
      const _n: never = op;
      return String(_n);
    }
  }
}

/** Checkout total — listed prices exclude VAT; payment adds 18%. */
function bookingCheckoutAmount(
  subtotal: number | null,
  currency: AssetLocaleCurrency,
): { payableTotal: number | null; amountLabel: string | null } {
  if (subtotal === null || !Number.isFinite(subtotal) || subtotal <= 0) {
    return { payableTotal: null, amountLabel: null };
  }
  const payableTotal = applyBookingVat(subtotal);
  const amountLabel =
    payableTotal !== null ? formatMoneyAmount(payableTotal, currency) : null;
  return { payableTotal, amountLabel };
}

export function BookingMenu({
  embedded = false,
  pageTheme = false,
}: {
  embedded?: boolean;
  pageTheme?: boolean;
} = {}) {
  const { t, language } = useI18n();
  const { userLoginType } = useAuth();
  const businessWorkerCheckout = isBusinessWorkerLogin(userLoginType);
  const privateClientCheckout = userLoginType === 'private client';
  const {
    openAssets,
    assetsBrowseParams,
    bookingsBrowseParams,
    bookingsTabFocus,
    openBookings,
    clearBookingsBrowseBookingId,
    registerBookingFlowGuard,
    navigationBack,
  } = useAppNavigation();
  const {
    pendingQuickBooking,
    clearPendingQuickBooking,
    pendingAssetQuickBook,
    clearPendingAssetQuickBook,
    requestQuickBooking,
  } = useBookingCreate();
  const { reportError } = useLastAppError();
  const { promptProfileCompletion } = useProfileBookingGate();
  const menuId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelScrollRef = useRef<HTMLDivElement>(null);
  const quickBookPanelRef = useRef<HTMLDivElement>(null);
  const bookingErrorRef = useRef<HTMLDivElement>(null);
  const bookingStatusNoticeRef = useRef<HTMLParagraphElement>(null);
  const bookingAssetPreviewErrorRef = useRef<HTMLParagraphElement>(null);
  const openedForPendingCreateRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [operation, setOperation] = useState<Operation>('getById');
  const [bookingId, setBookingId] = useState(DEMO_BOOKING_ID);
  const [createBody, setCreateBody] = useState<Record<string, unknown>>(() =>
    deepClone(defaultBookingCreateBody()),
  );
  const [modifyPriceInput, setModifyPriceInput] = useState('150');
  const [ownerCancelAnswer, setOwnerCancelAnswer] = useState(false);
  const [formEpoch, setFormEpoch] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | AssetRequestError | null>(null);
  const [statusNotice, setStatusNotice] = useState<string | null>(null);
  const [dismissedBookingRevision, setDismissedBookingRevision] = useState(0);
  const [failureContext, setFailureContext] = useState<BookingFailureContext | null>(null);
  const [response, setResponse] = useState<{ operation: Operation; data: unknown } | null>(null);
  const [assetPricingOptions, setAssetPricingOptions] = useState<AssetBookingPricingOption[]>([]);
  const [assetPricingStatus, setAssetPricingStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle',
  );
  const [selectedPricingTypeId, setSelectedPricingTypeId] = useState<number | null>(null);
  const [bookingPeriodCount, setBookingPeriodCount] = useState(1);
  /** When editing, the booking cannot be shortened below this many whole periods. */
  const [editMinPeriodCount, setEditMinPeriodCount] = useState(1);
  const [assetAddonOptions, setAssetAddonOptions] = useState<AssetBookingAddonOption[]>([]);
  const [addonsCatalog, setAddonsCatalog] = useState<Record<string, unknown>[]>([]);
  const [createBookingAsset, setCreateBookingAsset] = useState<Record<string, unknown> | null>(null);
  const [editingBookingId, setEditingBookingId] = useState<string | null>(null);
  /** Server end_date when edit started — PATCH must not resend it unless extending. */
  const [editingBookingOriginalEndDate, setEditingBookingOriginalEndDate] = useState<string | null>(
    null,
  );
  /** Add-on quantities on the booking when edit started (for pool + reserved max). */
  const [editingBookingReservedAddonQty, setEditingBookingReservedAddonQty] = useState<
    Map<number, number>
  >(() => new Map());
  const [cancellingBookingId, setCancellingBookingId] = useState<string | null>(null);
  const [bookingAssetPreview, setBookingAssetPreview] = useState<Record<string, unknown> | null>(null);
  const [bookingAssetPreviewLoading, setBookingAssetPreviewLoading] = useState(false);
  const [bookingAssetPreviewError, setBookingAssetPreviewError] = useState<string | null>(null);
  const bookingAssetPreviewFetchRef = useRef(0);
  const [createFlowOrigin, setCreateFlowOrigin] = useState<CreateFlowOrigin | null>(null);
  /** Set from asset-page quick booking — schedule + total must not be recomputed from a failed asset GET. */
  const [quickBookingMode, setQuickBookingMode] = useState<QuickBookingMode | null>(null);
  const [quickBookingLockedTotal, setQuickBookingLockedTotal] = useState<number | null>(null);
  const quickBookingUsesAssetSnapshotRef = useRef(false);
  const [assetReservedDayKeys, setAssetReservedDayKeys] = useState<Set<string>>(() => new Set());
  const [assetBookingsCalendarStatus, setAssetBookingsCalendarStatus] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle');
  const [createConfirmation, setCreateConfirmation] = useState<BookingConfirmationDetails | null>(
    null,
  );
  /** Pending unpaid booking — show pay CTA instead of “reservation successful”. */
  const [awaitingPaymentBookingId, setAwaitingPaymentBookingId] = useState<string | null>(null);
  const [awaitingPaymentBusy, setAwaitingPaymentBusy] = useState(false);
  /** Whether the payment page is shown (create flow, before the booking is created). */
  const [paymentOpen, setPaymentOpen] = useState(false);
  /** Business workers default to employer credits; they can switch to card/Bit/PayPal. */
  const [paymentCheckoutView, setPaymentCheckoutView] = useState<'worker-credits' | 'regular'>(
    'regular',
  );
  /** Cancellation policy must be accepted before payment when the booking has a payable total. */
  const [cancellationPolicyOpen, setCancellationPolicyOpen] = useState(false);
  /** Gym health declaration before checkout (first booking per year). */
  const [healthDeclarationOpen, setHealthDeclarationOpen] = useState(false);
  /** Parking garage: choose profile vehicle before checkout. */
  const [parkingVehicleOpen, setParkingVehicleOpen] = useState(false);
  /** Pre-formatted amount shown on the payment step (null when the total is unknown). */
  const [pendingPaymentAmount, setPendingPaymentAmount] = useState<string | null>(null);
  /** Private-client wallet balance label for checkout. */
  const [walletCreditsBalanceLabel, setWalletCreditsBalanceLabel] = useState<string | null>(null);
  /** Set once the user completes the payment step so the next submit creates the booking. */
  const paymentCompletedRef = useRef(false);
  /**
   * Card/Bit/PayPal (PayPlus Stage B): after create, generate payment link and open PayPlus
   * instead of treating the booking as paid.
   */
  const payplusGatewayCheckoutRef = useRef(false);
  /** Pending booking already created when generate-link failed — retry Pay without re-creating. */
  const payplusRetryBookingIdRef = useRef<string | null>(null);
  /** Force create total_price to ₪1 when PayPlus sandbox test card was used. */
  const payplusTestChargeRef = useRef(false);
  /** True once we hand off to PayPlus — skip bookings-list UI so it never flashes under the redirect. */
  const payplusNavigatingAwayRef = useRef(false);
  const onSubmitRef = useRef<() => Promise<boolean>>(async () => false);
  const holdCheckoutForRegistrationRef = useRef(false);
  /** Tracks whether period-based pricing was active — used to sync hour count from calendar picks. */
  const hadPeriodBasedSchedulingRef = useRef(false);
  const paymentFailureMessageRef = useRef<string | null>(null);
  /** Worker employer credits wire, or private-client `{ pay_with_credits: true }`. */
  const bookingCreditsPaymentRef = useRef<
    | ReturnType<typeof normalizeWorkerCreditsPaymentWire>
    | ReturnType<typeof normalizePrivateClientPayWithCreditsWire>
    | null
  >(null);
  const healthDeclarationSatisfiedRef = useRef(false);
  /** UUID from GET /profile vehicles — booking selection only (does not change is_primary). */
  const parkingVehicleIdRef = useRef<string | null>(null);
  const parkingVehicleSummaryRef = useRef<string | null>(null);
  const pendingCheckoutPayableRef = useRef<number | null>(null);
  const lastBookedAssetSnapshotRef = useRef<Record<string, unknown> | null>(null);
  /** Asset detail to reopen when leaving create flow that started from the assets tab. */
  const createFlowReturnAssetIdRef = useRef<string | null>(null);
  /** Avoid re-applying the same pending quick-book request within one BookingMenu mount. */
  const appliedAssetQuickBookNonceRef = useRef<number | null>(null);
  /** Shown when week/month/year end was rounded up to the next full billing period. */
  const [periodScheduleWarning, setPeriodScheduleWarning] = useState<string | null>(null);
  const [bookingsListTimeFilter, setBookingsListTimeFilter] =
    useState<BookingListTimeFilter>('currentAndFuture');

  const close = useCallback(() => setOpen(false), []);

  const createAssetId = useMemo(() => {
    const raw = createBody.asset_id;
    return typeof raw === 'string' ? raw.trim() : '';
  }, [createBody.asset_id]);

  const selectedBookingOption = useMemo(
    () =>
      selectedPricingTypeId === null
        ? null
        : assetPricingOptions.find((o) => o.pricingTypeId === selectedPricingTypeId) ?? null,
    [assetPricingOptions, selectedPricingTypeId],
  );

  const isEditingBooking = editingBookingId !== null;

  const isQuickBookingCreateFlow =
    createFlowOrigin === 'assets' && !isEditingBooking && quickBookingMode !== null;

  const effectiveQuickBookingMode = useMemo((): QuickBookingMode | null => {
    if (quickBookingMode) {
      return quickBookingMode;
    }
    if (createFlowOrigin !== 'assets') {
      return null;
    }
    const pricingTypeId = createBody.pricing_type_id;
    if (pricingTypeId === BOOKING_HOURLY_PRICING_TYPE_ID) {
      return 'hour';
    }
    if (pricingTypeId === BOOKING_DAILY_PRICING_TYPE_ID) {
      return 'day';
    }
    if (pricingTypeId === BOOKING_MONTHLY_PRICING_TYPE_ID) {
      return 'month';
    }
    return null;
  }, [quickBookingMode, createFlowOrigin, createBody.pricing_type_id]);

  const usePeriodBasedScheduling = Boolean(
    assetPricingStatus === 'ready' &&
      assetPricingOptions.length > 0 &&
      selectedBookingOption?.kind,
  );

  const isHourlySameDayBooking = usePeriodBasedScheduling && selectedBookingOption?.kind === 'hour';

  const showBookingCalendar =
    operation === 'create' &&
    Boolean(createAssetId) &&
    !isEditingBooking &&
    !isQuickBookingCreateFlow;

  const hideLegacyScheduleDatePickers = showBookingCalendar;

  const bookingStartDayKey = useMemo(
    () => isoToCalendarDayKey(createBody.start_date),
    [createBody.start_date],
  );
  const bookingEndDayKey = useMemo(
    () => isoToCalendarDayKey(createBody.end_date),
    [createBody.end_date],
  );

  useEffect(() => {
    if (!showBookingCalendar) {
      setAssetReservedDayKeys(new Set());
      setAssetBookingsCalendarStatus('idle');
      return;
    }
    let cancelled = false;
    setAssetBookingsCalendarStatus('loading');
    void (async () => {
      try {
        const data = await fetchBookingsList();
        if (cancelled) {
          return;
        }
        const records = normalizeBookingListRecords(data);
        setAssetReservedDayKeys(
          collectReservedDayKeysForAsset(records, createAssetId, {
            excludeBookingId: editingBookingId ?? undefined,
          }),
        );
        setAssetBookingsCalendarStatus('ready');
      } catch {
        if (!cancelled) {
          setAssetReservedDayKeys(new Set());
          setAssetBookingsCalendarStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showBookingCalendar, createAssetId, editingBookingId]);

  const showBookingTimeInputs = useMemo(() => {
    if (usePeriodBasedScheduling) {
      return selectedBookingOption?.kind === 'hour';
    }
    if (assetPricingStatus !== 'ready' || assetPricingOptions.length === 0) {
      return true;
    }
    return assetSupportsHourlyBooking(assetPricingOptions);
  }, [
    usePeriodBasedScheduling,
    selectedBookingOption,
    assetPricingStatus,
    assetPricingOptions,
  ]);

  const createScheduleValidation = useMemo(() => {
    if (createFlowOrigin === 'assets' && effectiveQuickBookingMode) {
      return {
        hourly: effectiveQuickBookingMode === 'hour',
        sameDayHourly: effectiveQuickBookingMode === 'hour',
        periodKind: effectiveQuickBookingMode,
      };
    }
    return {
      hourly: showBookingTimeInputs,
      sameDayHourly: isHourlySameDayBooking,
      periodKind:
        usePeriodBasedScheduling && selectedBookingOption?.kind
          ? selectedBookingOption.kind
          : undefined,
    };
  }, [
    createFlowOrigin,
    effectiveQuickBookingMode,
    showBookingTimeInputs,
    isHourlySameDayBooking,
    usePeriodBasedScheduling,
    selectedBookingOption,
  ]);

  const hourlyCalendarShowEndTimePicker = Boolean(
    showBookingTimeInputs &&
      showBookingCalendar &&
      !(usePeriodBasedScheduling && selectedBookingOption?.kind === 'hour'),
  );

  const calendarStartTime = useMemo(() => isoToDisplay(createBody.start_date).t, [createBody.start_date]);

  const calendarEndTime = useMemo(() => isoToDisplay(createBody.end_date).t, [createBody.end_date]);

  const maxHourlyPeriodCount = useMemo(() => {
    if (!isHourlySameDayBooking) {
      return null;
    }
    const startIso =
      typeof createBody.start_date === 'string' ? createBody.start_date.trim() : '';
    if (!startIso) {
      return null;
    }
    const max = maxHourlyPeriodCountSameDay(startIso);
    return max >= 1 ? max : null;
  }, [isHourlySameDayBooking, createBody.start_date]);

  const bookingsListResponseData = useMemo(() => {
    if (!response) {
      return null;
    }
    if (response.operation !== 'listAll') {
      return response.data;
    }
    return filterDismissedBookingsFromList(response.data);
  }, [response, dismissedBookingRevision]);

  const bookingListCount = useMemo(() => {
    if (!response) {
      return 0;
    }
    const records = normalizeBookingListRecords(bookingsListResponseData);
    if (response.operation === 'listAll') {
      return filterActiveMyBookingsListRecords(records).length;
    }
    return records.length;
  }, [response, bookingsListResponseData]);

  useEffect(() => {
    if (error) {
      return scheduleScrollPanelToElement(panelScrollRef, bookingErrorRef);
    }
    if (statusNotice) {
      return scheduleScrollPanelToElement(panelScrollRef, bookingStatusNoticeRef);
    }
    if (bookingAssetPreviewError) {
      return scheduleScrollPanelToElement(panelScrollRef, bookingAssetPreviewErrorRef);
    }
    return undefined;
  }, [error, statusNotice, bookingAssetPreviewError]);

  const operationEmptyHint = useMemo(() => emptyHintText(operation), [operation]);

  const ownerCannotBook = isAssetOwnerLoginType(userLoginType);
  const sessionCannotCreateBookings = !canCreateBookingsByLoginType(userLoginType);
  const bookingOperationChoices = useMemo(() => {
    const ops = bookingOperations(t);
    return sessionCannotCreateBookings ? ops.filter((op) => op.value !== 'create') : ops;
  }, [sessionCannotCreateBookings, t]);

  const useWidePanel = operation === 'create' || bookingListCount > 0;

  const selectedBookingAddonIds = useMemo(
    () => bookingSelectedAddonIds(createBody.addons),
    [createBody.addons],
  );

  const bookingAddonQuantities = useMemo(
    () => bookingAddonQuantitiesInBody(createBody.addons),
    [createBody.addons],
  );

  const computedBookingTotalPrice = useMemo(() => {
    if (operation !== 'create') {
      return null;
    }
    const startIso =
      typeof createBody.start_date === 'string' ? createBody.start_date.trim() : '';
    const endIso = typeof createBody.end_date === 'string' ? createBody.end_date.trim() : '';
    if (!startIso || !endIso) {
      return null;
    }
    return computeBookingWireTotalPrice({
      startIso,
      endIso,
      pricingOption: selectedBookingOption,
      addons: createBody.addons,
      addonChoices: assetAddonOptions,
    });
  }, [
    operation,
    createBody.start_date,
    createBody.end_date,
    createBody.addons,
    selectedBookingOption,
    assetAddonOptions,
  ]);

  useEffect(() => {
    if (operation !== 'create' || computedBookingTotalPrice === null || quickBookingLockedTotal !== null) {
      return;
    }
    setCreateBody((prev) => {
      if (prev.total_price === computedBookingTotalPrice) {
        return prev;
      }
      return { ...prev, total_price: computedBookingTotalPrice };
    });
  }, [operation, computedBookingTotalPrice, quickBookingLockedTotal]);

  useEffect(() => {
    if (!isHourlySameDayBooking || maxHourlyPeriodCount === null) {
      return;
    }
    if (bookingPeriodCount > maxHourlyPeriodCount) {
      setBookingPeriodCount(maxHourlyPeriodCount);
    }
  }, [isHourlySameDayBooking, maxHourlyPeriodCount, bookingPeriodCount]);

  const bookingAddonChoices = useMemo((): AssetBookingAddonOption[] => {
    if (createAssetId) {
      const base = assetAddonOptions;
      if (isEditingBooking && editingBookingReservedAddonQty.size > 0) {
        return applyBookingEditAddonAvailability(
          base,
          editingBookingReservedAddonQty,
          createBody.addons,
          addonsCatalog,
          language,
        );
      }
      return base;
    }
    if (addonsCatalog.length === 0) {
      return BOOKING_CREATE_ADDON_OPTIONS.map((o) => ({
        addonId: o.addonId,
        label: o.hint,
        price: null,
        maxQuantity: null,
      }));
    }
    const catalogById = new Map<number, Record<string, unknown>>();
    for (const row of addonsCatalog) {
      const raw = row.addon_id ?? row.id;
      const id =
        typeof raw === 'number' && Number.isFinite(raw)
          ? raw
          : typeof raw === 'string' && raw.trim()
            ? Number(raw.trim())
            : NaN;
      if (Number.isFinite(id) && !catalogById.has(id)) {
        catalogById.set(id, row);
      }
    }
    return BOOKING_CREATE_ADDON_OPTIONS.map((o) => {
      const row = catalogById.get(o.addonId);
      return {
        addonId: o.addonId,
        label: row ? addonOptionDisplayLabel({}, row, o.addonId, language) : o.hint,
        price: null,
        maxQuantity: null,
      };
    });
  }, [
    createAssetId,
    assetAddonOptions,
    addonsCatalog,
    isEditingBooking,
    editingBookingReservedAddonQty,
    createBody.addons,
    language,
  ]);

  const addonChoicesForErrors = useMemo(
    (): AssetBookingAddonOption[] =>
      bookingAddonChoices.length > 0 ? bookingAddonChoices : assetAddonOptions,
    [assetAddonOptions, bookingAddonChoices],
  );

  const formatBookingUserError = useCallback(
    (err: unknown): string => {
      let raw: string;
      if (err instanceof AssetRequestError) {
        raw = shortAssetRequestErrorSummary(err);
      } else if (err instanceof Error) {
        raw = err.message;
      } else {
        raw = String(err);
      }
      if (isIncompleteRegistrationBookingError(err) || isIncompleteRegistrationApiMessage(raw)) {
        return t('bookings.error.completeRegistration');
      }
      if (isBookingRoleDeniedApiMessage(raw)) {
        return t('bookings.error.roleNotAllowed');
      }
      if (isAssetNotBookableApiMessage(raw) || raw === 'ASSET_NOT_BOOKABLE') {
        return t('bookings.error.assetNotBookable');
      }
      const localizedSchedule = localizeBookingScheduleError(raw, t);
      if (localizedSchedule !== raw) {
        return localizedSchedule;
      }
      const localizedCapacity = localizeBookingPeopleCapacityError(raw, t);
      if (localizedCapacity !== raw) {
        return localizedCapacity;
      }
      return humanizeBookingAddonErrorMessage(raw, addonChoicesForErrors, addonsCatalog);
    },
    [addonChoicesForErrors, addonsCatalog, t],
  );

  const syncCreateBodyAddons = useCallback(
    (selectedIds: Set<number>, quantities: Map<number, number>) => {
      setCreateBody((prev) => ({
        ...prev,
        addons: buildBookingAddonsWirePayload(
          bookingAddonChoices,
          bookingAddonsBodyFromSelection(selectedIds, quantities),
        ),
      }));
    },
    [bookingAddonChoices],
  );

  const onBookingAddonToggle = useCallback(
    (addonId: number, checked: boolean) => {
      const opt = bookingAddonChoices.find((o) => o.addonId === addonId);
      if (!opt) {
        return;
      }
      const ids = bookingSelectedAddonIds(createBody.addons);
      const quantities = bookingAddonQuantitiesInBody(createBody.addons);
      if (checked) {
        ids.add(addonId);
        quantities.set(addonId, clampBookingAddonQuantity(1, opt.maxQuantity));
      } else {
        ids.delete(addonId);
        quantities.delete(addonId);
      }
      syncCreateBodyAddons(ids, quantities);
    },
    [bookingAddonChoices, createBody.addons, syncCreateBodyAddons],
  );

  const onBookingAddonQuantityChange = useCallback(
    (addonId: number, raw: string) => {
      const opt = bookingAddonChoices.find((o) => o.addonId === addonId);
      if (!opt) {
        return;
      }
      const n = Number(raw.trim());
      const ids = bookingSelectedAddonIds(createBody.addons);
      if (!ids.has(addonId)) {
        return;
      }
      const quantities = bookingAddonQuantitiesInBody(createBody.addons);
      quantities.set(
        addonId,
        Number.isFinite(n) && n >= 1
          ? clampBookingAddonQuantity(n, opt.maxQuantity)
          : clampBookingAddonQuantity(1, opt.maxQuantity),
      );
      syncCreateBodyAddons(ids, quantities);
    },
    [bookingAddonChoices, createBody.addons, syncCreateBodyAddons],
  );

  const proceedToCheckoutSteps = useCallback(
    (payableTotal: number | null, amountLabel: string | null) => {
      pendingCheckoutPayableRef.current = payableTotal;
      setPendingPaymentAmount(amountLabel);
      setHealthDeclarationOpen(false);
      const hasPayable =
        typeof payableTotal === 'number' && Number.isFinite(payableTotal) && payableTotal > 0;
      if (hasPayable) {
        setCancellationPolicyOpen(true);
        setPaymentOpen(false);
      } else {
        setCancellationPolicyOpen(false);
        setPaymentCheckoutView(businessWorkerCheckout ? 'worker-credits' : 'regular');
        setPaymentOpen(true);
      }
    },
    [businessWorkerCheckout],
  );

  const tryOpenCheckoutForGymBooking = useCallback(
    async (
      asset: Record<string, unknown> | null,
      payableTotal: number | null,
      amountLabel: string | null,
    ) => {
      if (asset && assetIsParkingBooking(asset) && !parkingVehicleIdRef.current) {
        pendingCheckoutPayableRef.current = payableTotal;
        setPendingPaymentAmount(amountLabel);
        setParkingVehicleOpen(true);
        setHealthDeclarationOpen(false);
        setCancellationPolicyOpen(false);
        setPaymentOpen(false);
        return;
      }
      if (!asset || !assetIsGymBooking(asset)) {
        proceedToCheckoutSteps(payableTotal, amountLabel);
        return;
      }
      if (healthDeclarationSatisfiedRef.current) {
        proceedToCheckoutSteps(payableTotal, amountLabel);
        return;
      }
      try {
        const completed = await fetchMedicalQuestionnaireCompleted();
        if (completed) {
          healthDeclarationSatisfiedRef.current = true;
          proceedToCheckoutSteps(payableTotal, amountLabel);
          return;
        }
      } catch {
        /* prompt when profile fetch fails */
      }
      pendingCheckoutPayableRef.current = payableTotal;
      setPendingPaymentAmount(amountLabel);
      setHealthDeclarationOpen(true);
      setParkingVehicleOpen(false);
      setCancellationPolicyOpen(false);
      setPaymentOpen(false);
    },
    [proceedToCheckoutSteps],
  );

  useEffect(() => {
    if (!pendingQuickBooking) {
      return;
    }
    if (sessionCannotCreateBookings) {
      clearPendingQuickBooking();
      return;
    }
    openedForPendingCreateRef.current = true;
    paymentCompletedRef.current = false;
    parkingVehicleIdRef.current = null;
    parkingVehicleSummaryRef.current = null;
    healthDeclarationSatisfiedRef.current = false;
    const draft = pendingQuickBooking;
    createFlowReturnAssetIdRef.current = draft.assetId.trim();
    setCreateFlowOrigin('assets');
    setOpen(true);
    setOperation('create');
    setEditingBookingId(null);
    setEditingBookingOriginalEndDate(null);
    setEditingBookingReservedAddonQty(new Map());
    const snapshot = draft.assetSnapshot;
    lastBookedAssetSnapshotRef.current = snapshot;
    quickBookingUsesAssetSnapshotRef.current = true;
    setQuickBookingMode(draft.mode);
    const lockedTotal = draft.createBody.total_price;
    setQuickBookingLockedTotal(
      typeof lockedTotal === 'number' && Number.isFinite(lockedTotal) ? lockedTotal : null,
    );
    setCreateBookingAsset(snapshot);
    const snapshotPricing = resolveHourDayBookingPricingOptions(snapshot, []);
    setAssetPricingOptions(snapshotPricing);
    setAssetAddonOptions(resolveAssetBookingAddonOptions(snapshot, [], language));
    setAssetPricingStatus('ready');
    setCreateBody(deepClone(draft.createBody));
    setSelectedPricingTypeId(draft.createBody.pricing_type_id);
    const periodKind = draft.mode;
    const startIso = draft.createBody.start_date;
    const endIso = draft.createBody.end_date;
    const periods = countBookingPeriodsBetween(startIso, endIso, periodKind);
    setBookingPeriodCount(periods !== null && periods >= 1 ? periods : 1);
    setFormEpoch((k) => k + 1);
    setError(null);
    setFailureContext(null);
    setResponse(null);
    const total = draft.createBody.total_price;
    const subtotal =
      typeof total === 'number' && Number.isFinite(total) && total > 0 ? total : null;
    const { payableTotal, amountLabel } = bookingCheckoutAmount(
      subtotal,
      resolveAssetLocaleCurrency(snapshot),
    );
    void tryOpenCheckoutForGymBooking(snapshot, payableTotal, amountLabel);
    clearPendingQuickBooking();
    scheduleScrollPanelToTop(panelScrollRef);
  }, [
    pendingQuickBooking,
    clearPendingQuickBooking,
    sessionCannotCreateBookings,
    language,
    tryOpenCheckoutForGymBooking,
  ]);

  useEffect(() => {
    if (!pendingAssetQuickBook) {
      return;
    }
    if (sessionCannotCreateBookings) {
      clearPendingAssetQuickBook();
      return;
    }
    const alreadyApplied = appliedAssetQuickBookNonceRef.current === pendingAssetQuickBook.nonce;
    // Remount / interrupted hydrate can leave createFlowOrigin set while createBookingAsset is null.
    // Re-apply the pending request instead of showing a false "not bookable" empty state.
    if (alreadyApplied && createFlowOrigin === 'assets' && createBookingAsset) {
      return;
    }
    appliedAssetQuickBookNonceRef.current = pendingAssetQuickBook.nonce;
    // Keep pendingAssetQuickBook until cancel/proceed — clearing it here loses the request
    // when BookingMenu remounts (view switch / Strict Mode) and leaves an empty create page.
    openedForPendingCreateRef.current = true;
    paymentCompletedRef.current = false;
    const snapshot = pendingAssetQuickBook.assetSnapshot;
    const bookingAssetId =
      assetRecordMutationId(snapshot) ?? pendingAssetQuickBook.assetId.trim();
    createFlowReturnAssetIdRef.current = bookingAssetId;
    setCreateFlowOrigin('assets');
    setOpen(true);
    setOperation('create');
    setEditingBookingId(null);
    setEditingBookingOriginalEndDate(null);
    setEditingBookingReservedAddonQty(new Map());
    lastBookedAssetSnapshotRef.current = snapshot;
    quickBookingUsesAssetSnapshotRef.current = true;
    setQuickBookingMode(null);
    setQuickBookingLockedTotal(null);
    setCreateBookingAsset(snapshot);
    const snapshotPricing = resolveHourDayBookingPricingOptions(snapshot, []);
    setAssetPricingOptions(snapshotPricing);
    setAssetAddonOptions(resolveAssetBookingAddonOptions(snapshot, [], language));
    setAssetPricingStatus('ready');
    const body = deepClone(defaultBookingCreateBody());
    body.asset_id = bookingAssetId;
    setCreateBody(body);
    setSelectedPricingTypeId(null);
    setBookingPeriodCount(1);
    setFormEpoch((k) => k + 1);
    setError(null);
    setFailureContext(null);
    setResponse(null);
    // Do not scroll to top — showAssetQuickBookPanel effect scrolls to the booking form.
  }, [
    pendingAssetQuickBook,
    clearPendingAssetQuickBook,
    sessionCannotCreateBookings,
    language,
    createFlowOrigin,
    createBookingAsset,
  ]);

  useEffect(() => {
    if (operation !== 'create' || !createAssetId) {
      if (!pendingAssetQuickBook && createFlowOrigin !== 'assets') {
        setAssetPricingOptions([]);
        setAssetPricingStatus('idle');
        setAssetAddonOptions([]);
        setCreateBookingAsset(null);
        setSelectedPricingTypeId(null);
        setBookingPeriodCount(1);
      }
      return;
    }
    let cancelled = false;
    const usingSnapshot = quickBookingUsesAssetSnapshotRef.current;
    const snapshotFallback = lastBookedAssetSnapshotRef.current;
    // Search/list Book always brings a snapshot — never block the panel on GET /pricing catalogs.
    if (usingSnapshot && snapshotFallback) {
      setCreateBookingAsset(snapshotFallback);
      setAssetPricingOptions(resolveHourDayBookingPricingOptions(snapshotFallback, []));
      setAssetPricingStatus('ready');
      setError(null);
    } else if (!usingSnapshot) {
      setAssetPricingStatus('loading');
    }
    (async () => {
      let asset: Record<string, unknown> | null = null;
      let pricingCatalog: Record<string, unknown>[] = [];
      let addonCatalog: Record<string, unknown>[] = [];

      // Separate fetches so pricing/addon catalog failures cannot wipe a bookable snapshot.
      try {
        const assetRaw = await getAssetById(createAssetId);
        if (
          assetRaw !== null &&
          typeof assetRaw === 'object' &&
          !Array.isArray(assetRaw)
        ) {
          asset = assetRaw as Record<string, unknown>;
        }
      } catch {
        asset = null;
      }

      try {
        pricingCatalog = normalizePricingTypesCatalog(await fetchAssetPricingTypes());
      } catch {
        pricingCatalog = [];
      }

      try {
        addonCatalog = normalizeAddonsCatalog(await fetchAssetAddons());
      } catch {
        addonCatalog = [];
      }

      if (cancelled) {
        return;
      }

      setAddonsCatalog(addonCatalog);

      const freshUsable = asset && assetRecordIsBookable(asset) ? asset : null;
      const base = freshUsable
        ? mergeBookingAssetSnapshot(snapshotFallback, freshUsable)
        : snapshotFallback;
      if (!base) {
        // Browse Book flow must keep the panel. Catalog/GET failure is not “delisted”.
        if (createFlowOrigin === 'assets') {
          setAssetPricingStatus('ready');
          quickBookingUsesAssetSnapshotRef.current = false;
          return;
        }
        setAssetPricingOptions([]);
        setCreateBookingAsset(null);
        setAssetPricingStatus('error');
        setError(t('bookings.error.assetNotBookable'));
        quickBookingUsesAssetSnapshotRef.current = false;
        return;
      }

      setCreateBookingAsset(base);
      lastBookedAssetSnapshotRef.current = base;
      setAssetPricingOptions(resolveHourDayBookingPricingOptions(base, pricingCatalog));
      setAssetAddonOptions(resolveAssetBookingAddonOptions(base, addonCatalog, language));
      setAssetPricingStatus('ready');
      setError(null);
      quickBookingUsesAssetSnapshotRef.current = false;
    })();
    return () => {
      cancelled = true;
    };
    // Do not depend on `t` or pendingAssetQuickBook — those change identity/content often and
    // re-trigger GET /assets/:id + catalogs in a tight loop (see Network staircase).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- create flow hydrate once per asset/epoch
  }, [operation, createAssetId, formEpoch, language, createFlowOrigin]);

  useEffect(() => {
    if (!createBookingAsset || addonsCatalog.length === 0) {
      return;
    }
    setAssetAddonOptions(resolveAssetBookingAddonOptions(createBookingAsset, addonsCatalog, language));
  }, [language, createBookingAsset, addonsCatalog]);

  useEffect(() => {
    if (operation !== 'create' || !open || createAssetId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const raw = await fetchAssetAddons();
        if (cancelled) {
          return;
        }
        setAddonsCatalog(normalizeAddonsCatalog(raw));
      } catch {
        if (!cancelled) {
          setAddonsCatalog([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [operation, open, createAssetId, formEpoch]);

  useEffect(() => {
    if (assetPricingStatus !== 'ready' || assetPricingOptions.length === 0) {
      return;
    }
    setSelectedPricingTypeId((prev) => {
      const bodyPt = createBody.pricing_type_id;
      if (
        typeof bodyPt === 'number' &&
        Number.isFinite(bodyPt) &&
        assetPricingOptions.some((o) => o.pricingTypeId === bodyPt)
      ) {
        return bodyPt;
      }
      if (isEditingBooking) {
        const resolved = resolveBookingPricingOptionForUpdate(
          assetPricingOptions,
          createBody,
        );
        if (resolved) {
          return resolved.pricingTypeId;
        }
      } else if (prev !== null && assetPricingOptions.some((o) => o.pricingTypeId === prev)) {
        return prev;
      }
      return assetPricingOptions[0]?.pricingTypeId ?? null;
    });
  }, [
    assetPricingStatus,
    assetPricingOptions,
    isEditingBooking,
    createBody,
    createBody.pricing_type_id,
    createBody.start_date,
    createBody.end_date,
    createBody.total_price,
  ]);

  useEffect(() => {
    if (operation !== 'create' || !createAssetId || assetPricingStatus !== 'ready') {
      return;
    }
    const ids = bookingSelectedAddonIds(createBody.addons);
    if (ids.size === 0) {
      return;
    }
    const nextAddons = buildBookingAddonsWirePayload(bookingAddonChoices, createBody.addons);
    const prevJson = JSON.stringify(createBody.addons ?? []);
    const nextJson = JSON.stringify(nextAddons);
    if (prevJson !== nextJson) {
      setCreateBody((prev) => ({ ...prev, addons: nextAddons }));
    }
  }, [operation, createAssetId, assetPricingStatus, bookingAddonChoices, createBody.addons]);

  useEffect(() => {
    if (operation !== 'create' || usePeriodBasedScheduling) {
      return;
    }
    const startIso = typeof createBody.start_date === 'string' ? createBody.start_date.trim() : '';
    const endIso = typeof createBody.end_date === 'string' ? createBody.end_date.trim() : '';
    if (!startIso || !endIso) {
      return;
    }
    const startDt = new Date(startIso);
    const endDt = new Date(endIso);
    if (Number.isNaN(startDt.getTime()) || Number.isNaN(endDt.getTime()) || endDt > startDt) {
      return;
    }
    const startDisplay = isoToDisplay(startIso);
    const nextEndIso = showBookingTimeInputs
      ? addBookingPeriods(startIso, 'hour', 1)
      : displayToIsoWithDefaults(
          startDisplay,
          resolveBookingEndDefaultTime(startDisplay, false),
        );
    if (!nextEndIso || nextEndIso === endIso) {
      return;
    }
    setCreateBody((prev) => setAtPath(prev, ['end_date'], nextEndIso));
  }, [
    operation,
    usePeriodBasedScheduling,
    createBody.start_date,
    createBody.end_date,
    showBookingTimeInputs,
  ]);

  useEffect(() => {
    if (operation !== 'create' || !createAssetId) {
      hadPeriodBasedSchedulingRef.current = false;
    }
  }, [operation, createAssetId]);

  useEffect(() => {
    const active =
      operation === 'create' &&
      usePeriodBasedScheduling &&
      Boolean(selectedBookingOption?.kind);
    if (active && !hadPeriodBasedSchedulingRef.current) {
      const startIso =
        typeof createBody.start_date === 'string' ? createBody.start_date.trim() : '';
      const endIso =
        typeof createBody.end_date === 'string' ? createBody.end_date.trim() : '';
      if (startIso && endIso && selectedBookingOption?.kind) {
        const count = countBookingPeriodsBetween(startIso, endIso, selectedBookingOption.kind);
        if (count !== null && count >= 1) {
          setBookingPeriodCount(count);
        }
      }
    }
    hadPeriodBasedSchedulingRef.current = active;
  }, [
    operation,
    usePeriodBasedScheduling,
    selectedBookingOption,
    createBody.start_date,
    createBody.end_date,
  ]);

  useEffect(() => {
    if (!isEditingBooking || !selectedBookingOption?.kind) {
      return;
    }
    const startIso = typeof createBody.start_date === 'string' ? createBody.start_date.trim() : '';
    const endIso = typeof createBody.end_date === 'string' ? createBody.end_date.trim() : '';
    if (!startIso || !endIso) {
      return;
    }
    const count = countBookingPeriodsBetween(startIso, endIso, selectedBookingOption.kind);
    if (count !== null && count >= 1) {
      setEditMinPeriodCount(count);
      setBookingPeriodCount(count);
    }
  }, [isEditingBooking, selectedBookingOption, formEpoch]);

  useEffect(() => {
    if (operation !== 'create' || !usePeriodBasedScheduling || !selectedBookingOption?.kind) {
      return;
    }
    const startIso = typeof createBody.start_date === 'string' ? createBody.start_date.trim() : '';
    if (!startIso) {
      return;
    }
    const endIso = addBookingPeriods(startIso, selectedBookingOption.kind, bookingPeriodCount);
    if (!endIso) {
      return;
    }
    setCreateBody((prev) => {
      if (prev.end_date === endIso) {
        return prev;
      }
      return setAtPath(prev, ['end_date'], endIso);
    });
  }, [
    operation,
    isEditingBooking,
    usePeriodBasedScheduling,
    selectedBookingOption,
    bookingPeriodCount,
    createBody.start_date,
  ]);

  useEffect(() => {
    if (!open || embedded) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    const onPointerDown = (e: globalThis.MouseEvent) => {
      const el = wrapRef.current;
      if (el && !el.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, close, embedded]);

  const onOperationChange = (next: Operation) => {
    setOperation(next);
    setError(null);
    setFailureContext(null);
    setResponse(null);
    setEditingBookingId(null);
    setEditingBookingOriginalEndDate(null);
    setEditingBookingReservedAddonQty(new Map());
    if (next !== 'create') {
      setCreateFlowOrigin(null);
    }
    if (next === 'create') {
      setCreateBody(deepClone(defaultBookingCreateBody()));
      setSelectedPricingTypeId(null);
      setBookingPeriodCount(1);
      setPeriodScheduleWarning(null);
    }
  };

  const refreshBookingsList = useCallback(async () => {
    const data = await fetchBookingsList();
    setResponse({ operation: 'listAll', data: filterDismissedBookingsFromList(data) });
    setError(null);
    setFailureContext(null);
  }, []);

  const completeClientBookingCancel = useCallback(
    async (bookingId: string) => {
      const result = await cancelBookingAsClientResilient(bookingId);
      if (result.mode === 'dismissed_orphan') {
        setDismissedBookingRevision((n) => n + 1);
        setStatusNotice(t('bookings.cancelOrphanSuccess'));
      } else {
        setStatusNotice(t('bookings.cancelSuccess'));
      }
      setError(null);
      setFailureContext(null);
      setOperation('listAll');
      await refreshBookingsList();
    },
    [refreshBookingsList, t],
  );

  const onCancelBookingEdit = useCallback(() => {
    setEditingBookingId(null);
    setEditingBookingOriginalEndDate(null);
    setEditingBookingReservedAddonQty(new Map());
    setCreateBody(deepClone(defaultBookingCreateBody()));
    setSelectedPricingTypeId(null);
    setBookingPeriodCount(1);
    setEditMinPeriodCount(1);
    setFormEpoch((k) => k + 1);
    setOperation('listAll');
    setError(null);
    setFailureContext(null);
    setPeriodScheduleWarning(null);
    setPaymentOpen(false);
    setCancellationPolicyOpen(false);
    setHealthDeclarationOpen(false);
    setPendingPaymentAmount(null);
    paymentCompletedRef.current = false;
  }, []);

  const onUpdateBookingFromList = useCallback((row: Record<string, unknown>) => {
    const id = bookingRecordPrimaryId(row) ?? extractBookingIdFromApiPayload(row);
    if (!id || !bookingRecordCanMutate(row)) {
      return;
    }
    clearBookingsBrowseBookingId();
    bookingAssetPreviewFetchRef.current += 1;
    setBookingAssetPreview(null);
    setBookingAssetPreviewLoading(false);
    setBookingAssetPreviewError(null);
    setCreateConfirmation(null);
    setAwaitingPaymentBookingId(null);
    setPaymentOpen(false);
    setCancellationPolicyOpen(false);
    setHealthDeclarationOpen(false);
    setPendingPaymentAmount(null);
    paymentCompletedRef.current = false;
    setOpen(true);
    setOperation('create');
    setEditingBookingId(id);
    const originalEnd =
      typeof row.end_date === 'string' && row.end_date.trim() ? row.end_date.trim() : null;
    setEditingBookingOriginalEndDate(originalEnd);
    const editBody = bookingUpdateBodyFromRecord(row);
    setEditingBookingReservedAddonQty(extractBookingAddonQuantitiesFromBody(editBody.addons));
    setBookingId(id);
    setCreateBody(deepClone(editBody));
    setSelectedPricingTypeId(coercePricingTypeIdFromRecord(row));
    setBookingPeriodCount(1);
    setEditMinPeriodCount(1);
    setFormEpoch((k) => k + 1);
    setError(null);
    setFailureContext(null);
    setPeriodScheduleWarning(null);
    const panel = panelScrollRef.current;
    if (panel) {
      if (typeof panel.scrollTo === 'function') {
        panel.scrollTo({ top: 0, behavior: 'auto' });
      } else {
        panel.scrollTop = 0;
      }
    }
  }, [clearBookingsBrowseBookingId]);

  const onViewAssetFromList = useCallback(
    (row: Record<string, unknown>) => {
      const assetId = bookingRecordAssetId(row);
      if (!assetId) {
        return;
      }
      const nested = bookingRecordNestedAsset(row);
      if (nested) {
        rememberBrowseAssetListSnapshot(nested);
      }
      const snapshot = nested ? prepareFavoriteAssetSnapshot(nested) : null;
      setBookingAssetPreview(snapshot);
      setBookingAssetPreviewError(null);
      setBookingAssetPreviewLoading(true);
      const gen = ++bookingAssetPreviewFetchRef.current;
      void (async () => {
        try {
          const loaded = await getAssetById(assetId);
          if (gen !== bookingAssetPreviewFetchRef.current) {
            return;
          }
          if (!isPlainObject(loaded)) {
            if (!snapshot) {
              setBookingAssetPreviewError(t('assets.cannotReadResponse'));
            }
            return;
          }
          const merged = snapshot
            ? prepareFavoriteAssetSnapshot(mergeAssetApiRecordWithListSnapshot(loaded, snapshot))
            : prepareFavoriteAssetSnapshot(loaded);
          setBookingAssetPreview(merged);
        } catch (err) {
          if (gen !== bookingAssetPreviewFetchRef.current) {
            return;
          }
          if (!snapshot) {
            setBookingAssetPreviewError(
              err instanceof Error ? err.message : t('common.somethingWrong'),
            );
          }
        } finally {
          if (gen === bookingAssetPreviewFetchRef.current) {
            setBookingAssetPreviewLoading(false);
          }
        }
      })();
    },
    [t],
  );

  const clearBookingAssetPreview = useCallback(() => {
    bookingAssetPreviewFetchRef.current += 1;
    setBookingAssetPreview(null);
    setBookingAssetPreviewLoading(false);
    setBookingAssetPreviewError(null);
  }, []);

  const onCancelBookingFromList = useCallback(
    async (row: Record<string, unknown>) => {
      const id = bookingRecordPrimaryId(row);
      if (!id || !bookingRecordCanMutate(row)) {
        return;
      }
      const label = bookingRecordAssetName(row);
      const dates = bookingRecordDateRangeLabel(row, language);
      if (
        !window.confirm(
          `Cancel reservation for “${label}” (${dates})? This cannot be undone.`,
        )
      ) {
        return;
      }
      setCancellingBookingId(id);
      setError(null);
      setFailureContext(null);
      setStatusNotice(null);
      try {
        await completeClientBookingCancel(id);
      } catch (err) {
        setFailureContext({ operation: 'cancelClient', bookingId: id });
        if (err instanceof AssetRequestError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err.message : 'Something went wrong.');
        }
      } finally {
        setCancellingBookingId(null);
      }
    },
    [completeClientBookingCancel, language],
  );

  const onCreateFieldChange = useCallback((path: string[], value: unknown) => {
    setCreateBody((prev) => setAtPath(prev, path, value));
  }, []);

  const scheduleFieldsResetKey = `${formEpoch}-${operation}`;

  const onStartScheduleCommitted = useCallback((iso: string) => {
    setCreateBody((prev) => setAtPath(prev, ['start_date'], iso));
  }, []);

  const onEndScheduleCommitted = useCallback((iso: string) => {
    setCreateBody((prev) => setAtPath(prev, ['end_date'], iso));
  }, []);

  const resolveStartDefaultTime = useCallback(
    (display: ScheduleDisplay) => resolveBookingStartDefaultTime(display, showBookingTimeInputs),
    [showBookingTimeInputs],
  );

  const resolveEndDefaultTime = useCallback(
    (display: ScheduleDisplay) => resolveBookingEndDefaultTime(display, showBookingTimeInputs),
    [showBookingTimeInputs],
  );

  const startSchedule = useScheduleField(
    createBody.start_date,
    resolveStartDefaultTime,
    onStartScheduleCommitted,
    scheduleFieldsResetKey,
    { clampHourlyStartOnToday: showBookingTimeInputs },
  );
  const endSchedule = useScheduleField(
    createBody.end_date,
    resolveEndDefaultTime,
    onEndScheduleCommitted,
    scheduleFieldsResetKey,
  );

  const buildPeriodRoundUpWarning = useCallback(
    (kind: NonNullable<AssetBookingPricingOption['kind']>, periodCount: number, alignedEndIso: string) =>
      t('bookings.periodEndRoundedUp', {
        end: formatBookingInstantPreview(alignedEndIso, showBookingTimeInputs),
        periods: formatBookingPeriodCountLabel(t, kind, periodCount),
      }),
    [t, showBookingTimeInputs],
  );

  const commitPeriodScheduleFromIsos = useCallback(
    (
      startIso: string,
      endIso: string,
      startKey: string,
      endKey: string,
      minPeriodCount: number,
    ): boolean => {
      if (!usePeriodBasedScheduling || !selectedBookingOption?.kind) {
        return false;
      }
      const resolved = resolveBookingPeriodSchedule(
        startIso,
        endIso,
        selectedBookingOption.kind,
      );
      if (!resolved || resolved.periodCount < minPeriodCount) {
        return false;
      }
      const alignedEndKey = isoToCalendarDayKey(resolved.alignedEndIso) ?? endKey;
      if (rangeIncludesReservedDay(startKey, alignedEndKey, assetReservedDayKeys)) {
        return false;
      }
      setBookingPeriodCount(resolved.periodCount);
      setPeriodScheduleWarning(
        resolved.roundedUp
          ? buildPeriodRoundUpWarning(
              selectedBookingOption.kind,
              resolved.periodCount,
              resolved.alignedEndIso,
            )
          : null,
      );
      setCreateBody((prev) =>
        setAtPath(
          setAtPath(prev, ['start_date'], startIso),
          ['end_date'],
          resolved.alignedEndIso,
        ),
      );
      return true;
    },
    [
      assetReservedDayKeys,
      buildPeriodRoundUpWarning,
      selectedBookingOption,
      usePeriodBasedScheduling,
    ],
  );

  const applyCalendarRange = useCallback(
    (startKey: string, endKey: string) => {
      const startParts = calendarDayKeyToScheduleDisplay(startKey);
      const endParts = calendarDayKeyToScheduleDisplay(endKey);
      if (!startParts || !endParts) {
        return;
      }
      const startDisplay: ScheduleDisplay = { ...startParts, t: '' };
      const endDisplay: ScheduleDisplay = { ...endParts, t: '' };
      const startIso = displayToIsoWithDefaults(startDisplay, resolveStartDefaultTime(startDisplay), {
        clampHourlyStartOnToday: showBookingTimeInputs,
      });
      const endIso = displayToIsoWithDefaults(endDisplay, resolveEndDefaultTime(endDisplay));
      if (!startIso || !endIso) {
        return;
      }

      if (
        commitPeriodScheduleFromIsos(startIso, endIso, startKey, endKey, 1)
      ) {
        return;
      }
      if (usePeriodBasedScheduling && selectedBookingOption?.kind) {
        return;
      }

      setPeriodScheduleWarning(null);
      setCreateBody((prev) =>
        setAtPath(setAtPath(prev, ['start_date'], startIso), ['end_date'], endIso),
      );
    },
    [
      commitPeriodScheduleFromIsos,
      resolveEndDefaultTime,
      resolveStartDefaultTime,
      selectedBookingOption,
      showBookingTimeInputs,
      usePeriodBasedScheduling,
    ],
  );

  const onCalendarStartDaySelect = useCallback(
    (dayKey: string) => {
      if (assetReservedDayKeys.has(dayKey)) {
        return;
      }
      const startParts = calendarDayKeyToScheduleDisplay(dayKey);
      if (!startParts) {
        return;
      }
      const startDisplay: ScheduleDisplay = { ...startParts, t: '' };
      const startIso = displayToIsoWithDefaults(startDisplay, resolveStartDefaultTime(startDisplay), {
        clampHourlyStartOnToday: showBookingTimeInputs,
      });
      if (!startIso) {
        return;
      }
      setPeriodScheduleWarning(null);
      setCreateBody((prev) => {
        if (isHourlySameDayBooking) {
          return setAtPath(prev, ['start_date'], startIso);
        }
        let next = setAtPath(prev, ['start_date'], startIso);
        const endDay = isoToCalendarDayKey(prev.end_date);
        if (endDay && endDay < dayKey) {
          next = setAtPath(next, ['end_date'], '');
        }
        return next;
      });
    },
    [
      assetReservedDayKeys,
      isHourlySameDayBooking,
      resolveStartDefaultTime,
      showBookingTimeInputs,
    ],
  );

  const onCalendarEndDaySelect = useCallback(
    (dayKey: string) => {
      if (isEditingBooking) {
        const startKey = bookingStartDayKey;
        if (!startKey) {
          return;
        }
        const end = dayKey;
        if (end < startKey) {
          return;
        }
        const minEndKey = editingBookingOriginalEndDate
          ? isoToCalendarDayKey(editingBookingOriginalEndDate)
          : startKey;
        if (minEndKey && end < minEndKey) {
          return;
        }
        if (rangeIncludesReservedDay(startKey, end, assetReservedDayKeys)) {
          return;
        }
        const endParts = calendarDayKeyToScheduleDisplay(end);
        if (!endParts) {
          return;
        }
        const endDisplay: ScheduleDisplay = { ...endParts, t: '' };
        const startIso =
          typeof createBody.start_date === 'string' ? createBody.start_date.trim() : '';
        const endIso = displayToIsoWithDefaults(endDisplay, resolveEndDefaultTime(endDisplay));
        if (!startIso || !endIso) {
          return;
        }
        if (
          commitPeriodScheduleFromIsos(
            startIso,
            endIso,
            startKey,
            end,
            editMinPeriodCount,
          )
        ) {
          return;
        }
        if (usePeriodBasedScheduling && selectedBookingOption?.kind) {
          return;
        }
        setPeriodScheduleWarning(null);
        setCreateBody((prev) => setAtPath(prev, ['end_date'], endIso));
        return;
      }
      const startKey = bookingStartDayKey ?? dayKey;
      const { start, end } = normalizeCalendarRangeKeys(startKey, dayKey);
      if (rangeIncludesReservedDay(start, end, assetReservedDayKeys)) {
        return;
      }
      applyCalendarRange(start, end);
    },
    [
      applyCalendarRange,
      assetReservedDayKeys,
      bookingStartDayKey,
      commitPeriodScheduleFromIsos,
      isEditingBooking,
      editingBookingOriginalEndDate,
      createBody.start_date,
      usePeriodBasedScheduling,
      selectedBookingOption,
      editMinPeriodCount,
      resolveEndDefaultTime,
    ],
  );

  const onCalendarStartTimeChange = useCallback(
    (time: string) => {
      if (!/^\d{2}:\d{2}$/.test(time)) {
        return;
      }
      setCreateBody((prev) => {
        const display = isoToDisplay(prev.start_date);
        if (!display.y || !display.m || !display.d) {
          return prev;
        }
        const withTime: ScheduleDisplay = { ...display, t: time };
        const clamped = clampHourlyStartTimeOnToday(withTime, time);
        const iso = displayToIso({ ...withTime, t: clamped });
        if (!iso) {
          return prev;
        }
        return setAtPath(prev, ['start_date'], iso);
      });
    },
    [],
  );

  const applyPeriodCountEndDate = useCallback(
    (body: Record<string, unknown>): Record<string, unknown> => {
      if (!usePeriodBasedScheduling || !selectedBookingOption?.kind) {
        return body;
      }
      const startIso = typeof body.start_date === 'string' ? body.start_date.trim() : '';
      if (!startIso) {
        return body;
      }
      const endIso = bookingEndIsoForPeriodCount(
        startIso,
        selectedBookingOption.kind,
        bookingPeriodCount,
      );
      if (!endIso || body.end_date === endIso) {
        return body;
      }
      return { ...body, end_date: endIso };
    },
    [bookingPeriodCount, selectedBookingOption, usePeriodBasedScheduling],
  );

  const onCalendarEndTimeChange = useCallback(
    (time: string) => {
      if (!/^\d{2}:\d{2}$/.test(time)) {
        return;
      }
      const endDisplay = isoToDisplay(createBody.end_date);
      if (!endDisplay.y || !endDisplay.m || !endDisplay.d) {
        return;
      }
      const iso = displayToIso({ ...endDisplay, t: time });
      if (!iso) {
        return;
      }
      if (usePeriodBasedScheduling && selectedBookingOption?.kind === 'hour') {
        const startIso =
          typeof createBody.start_date === 'string' ? createBody.start_date.trim() : '';
        if (startIso) {
          const count = countBookingPeriodsBetween(startIso, iso, 'hour');
          if (count !== null && count >= 1) {
            setBookingPeriodCount(count);
          }
        }
      }
      setCreateBody((prev) => setAtPath(prev, ['end_date'], iso));
    },
    [
      createBody.end_date,
      createBody.start_date,
      selectedBookingOption,
      usePeriodBasedScheduling,
    ],
  );

  const onClear = useCallback(() => {
    setError(null);
    setStatusNotice(null);
    setFailureContext(null);
    setResponse(null);
    setBookingId('');
    setModifyPriceInput('150');
    setOwnerCancelAnswer(false);
    setFormEpoch((k) => k + 1);
    setSelectedPricingTypeId(null);
    setBookingPeriodCount(1);
    setEditingBookingId(null);
    setEditingBookingOriginalEndDate(null);
    setEditingBookingReservedAddonQty(new Map());
    setCreateFlowOrigin(null);
    if (operation === 'create') {
      setCreateBody(emptyBookingCreateBody());
    }
  }, [operation]);

  const onClearButtonClick = useCallback(
    (e: ReactMouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onClear();
    },
    [onClear],
  );

  const runBookingsListFetch = useCallback(async () => {
    setError(null);
    setFailureContext(null);
    setResponse(null);
    setLoading(true);
    try {
      const data = await fetchBookingsList();
      setResponse({ operation: 'listAll', data: filterDismissedBookingsFromList(data) });
    } catch (err) {
      setResponse({ operation: 'listAll', data: [] });
      setFailureContext({ operation: 'listAll', bookingId: '' });
      if (err instanceof AssetRequestError) {
        setError(err);
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const resetCreateFlowDraft = useCallback(() => {
    setEditingBookingId(null);
    setEditingBookingOriginalEndDate(null);
    setEditingBookingReservedAddonQty(new Map());
    setCreateBody(deepClone(defaultBookingCreateBody()));
    setSelectedPricingTypeId(null);
    setBookingPeriodCount(1);
    setFormEpoch((k) => k + 1);
    setError(null);
    setFailureContext(null);
    setResponse(null);
  }, []);

  const openLastBookedAssetDetail = useCallback(
    (assetId: string | null | undefined, options?: { replace?: boolean }) => {
      const id = typeof assetId === 'string' ? assetId.trim() : '';
      openAssets(
        {
          assetTypeId: assetsBrowseParams.assetTypeId,
          assetId: id || null,
        },
        {
          preserveBrowseState: Boolean(id),
          replace: options?.replace,
        },
      );
    },
    [openAssets, assetsBrowseParams.assetTypeId],
  );

  /**
   * Close checkout UI and put the asset under the history entry before PayPlus.
   * Avoids a frozen "Processing…" payment sheet when the user presses Back.
   */
  const prepareUiBeforePayplusRedirect = useCallback(
    (returnAssetId: string | null) => {
      payplusNavigatingAwayRef.current = true;
      paymentCompletedRef.current = false;
      payplusGatewayCheckoutRef.current = false;
      payplusTestChargeRef.current = false;
      bookingCreditsPaymentRef.current = null;
      holdCheckoutForRegistrationRef.current = false;
      parkingVehicleIdRef.current = null;
      parkingVehicleSummaryRef.current = null;
      setLoading(false);
      setPaymentOpen(false);
      setCancellationPolicyOpen(false);
      setHealthDeclarationOpen(false);
      setParkingVehicleOpen(false);
      setPendingPaymentAmount(null);
      setAwaitingPaymentBusy(false);
      registerBookingFlowGuard(null);
      createFlowReturnAssetIdRef.current = null;
      setCreateFlowOrigin(null);
      setQuickBookingMode(null);
      setQuickBookingLockedTotal(null);
      quickBookingUsesAssetSnapshotRef.current = false;
      clearPendingAssetQuickBook();
      clearPendingQuickBooking();
      resetCreateFlowDraft();
      setOperation('listAll');
      if (returnAssetId) {
        openLastBookedAssetDetail(returnAssetId, { replace: true });
      }
      plantPayplusBrowserBackToAsset(returnAssetId);
    },
    [
      clearPendingAssetQuickBook,
      clearPendingQuickBooking,
      openLastBookedAssetDetail,
      registerBookingFlowGuard,
      resetCreateFlowDraft,
    ],
  );

  // Browser Back from PayPlus can restore a bfcache snapshot mid-checkout — clear stuck UI.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const onPageShow = (event: PageTransitionEvent) => {
      if (!payplusNavigatingAwayRef.current && !event.persisted) {
        return;
      }
      payplusNavigatingAwayRef.current = false;
      setLoading(false);
      setPaymentOpen(false);
      setCancellationPolicyOpen(false);
      setHealthDeclarationOpen(false);
      setParkingVehicleOpen(false);
      setPendingPaymentAmount(null);
      setAwaitingPaymentBusy(false);
      const pending = readPayplusPendingCheckout();
      const assetId = pending?.returnAssetId?.trim() || null;
      if (assetId) {
        openLastBookedAssetDetail(assetId, { replace: true });
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [openLastBookedAssetDetail]);

  const exitCreateFlowToOrigin = useCallback(
    (options?: { confirm?: boolean }) => {
      if (!createFlowOrigin) {
        return;
      }
      if (options?.confirm) {
        const discardLabel = editingBookingId
          ? t('bookings.discardEdit')
          : t('bookings.discardCreate');
        if (!window.confirm(discardLabel)) {
          return;
        }
      }
      const origin = createFlowOrigin;
      const returnAssetId = createFlowReturnAssetIdRef.current;
      createFlowReturnAssetIdRef.current = null;
      appliedAssetQuickBookNonceRef.current = null;
      clearPendingAssetQuickBook();
      clearPendingQuickBooking();
      setCreateFlowOrigin(null);
      resetCreateFlowDraft();
      setOperation('listAll');
      if (origin === 'assets') {
        // Pop /bookings so Back from the restored asset/list does not land on My Bookings.
        // Clear the leave-guard first — otherwise popstate would confirm and abandonFlow.
        registerBookingFlowGuard(null);
        if (typeof window !== 'undefined' && window.history.length > 1) {
          navigationBack();
        } else {
          openLastBookedAssetDetail(returnAssetId, { replace: true });
        }
      } else {
        void runBookingsListFetch();
      }
    },
    [
      createFlowOrigin,
      editingBookingId,
      openLastBookedAssetDetail,
      resetCreateFlowDraft,
      runBookingsListFetch,
      clearPendingAssetQuickBook,
      clearPendingQuickBooking,
      registerBookingFlowGuard,
      navigationBack,
      t,
    ],
  );

  const onBackFromListCreateFlow = useCallback(() => {
    exitCreateFlowToOrigin();
  }, [exitCreateFlowToOrigin]);

  const onCancelFromListCreateFlow = useCallback(() => {
    exitCreateFlowToOrigin({ confirm: true });
  }, [exitCreateFlowToOrigin]);

  const onAssetQuickBookProceed = useCallback(
    (draft: QuickBookingDraft) => {
      clearPendingAssetQuickBook();
      appliedAssetQuickBookNonceRef.current = null;
      requestQuickBooking(draft);
    },
    [clearPendingAssetQuickBook, requestQuickBooking],
  );

  const onAssetQuickBookCancel = useCallback(() => {
    clearPendingAssetQuickBook();
    exitCreateFlowToOrigin();
  }, [clearPendingAssetQuickBook, exitCreateFlowToOrigin]);

  const showBookingsListFromTab = useCallback(() => {
    clearPendingQuickBooking();
    clearPendingAssetQuickBook();
    appliedAssetQuickBookNonceRef.current = null;
    clearBookingsBrowseBookingId();
    setPaymentOpen(false);
    setCancellationPolicyOpen(false);
    setHealthDeclarationOpen(false);
    setPendingPaymentAmount(null);
    paymentCompletedRef.current = false;
    setCreateConfirmation(null);
    setCreateFlowOrigin(null);
    setQuickBookingMode(null);
    setQuickBookingLockedTotal(null);
    quickBookingUsesAssetSnapshotRef.current = false;
    lastBookedAssetSnapshotRef.current = null;
    createFlowReturnAssetIdRef.current = null;
    setEditingBookingId(null);
    setEditingBookingOriginalEndDate(null);
    setEditingBookingReservedAddonQty(new Map());
    setCreateBody(deepClone(defaultBookingCreateBody()));
    setSelectedPricingTypeId(null);
    setBookingPeriodCount(1);
    setEditMinPeriodCount(1);
    setFormEpoch((k) => k + 1);
    setPeriodScheduleWarning(null);
    setOperation('listAll');
    setOpen(true);
    setError(null);
    setFailureContext(null);
    setResponse(null);
    const panel = panelScrollRef.current;
    if (panel) {
      if (typeof panel.scrollTo === 'function') {
        panel.scrollTo({ top: 0, behavior: 'auto' });
      } else {
        panel.scrollTop = 0;
      }
    }
    void runBookingsListFetch();
  }, [clearPendingQuickBooking, clearPendingAssetQuickBook, clearBookingsBrowseBookingId, runBookingsListFetch]);

  useEffect(() => {
    const wantId = bookingsBrowseParams.bookingId?.trim();
    if (!embedded || !wantId || pendingQuickBooking || pendingAssetQuickBook) {
      return;
    }
    if (openedForPendingCreateRef.current || createFlowOrigin !== null || operation === 'create') {
      return;
    }
    setOperation('listAll');
    setOpen(true);
    void runBookingsListFetch();
  }, [
    embedded,
    bookingsBrowseParams.bookingId,
    pendingQuickBooking,
    pendingAssetQuickBook,
    createFlowOrigin,
    operation,
    runBookingsListFetch,
  ]);

  useEffect(() => {
    if (!embedded) {
      return;
    }
    setOpen(true);
    if (pendingQuickBooking || pendingAssetQuickBook) {
      return;
    }
    if (openedForPendingCreateRef.current || createFlowOrigin !== null || operation === 'create') {
      return;
    }
    setOperation('listAll');
    void runBookingsListFetch();
  }, [embedded, runBookingsListFetch, pendingQuickBooking, pendingAssetQuickBook, createFlowOrigin, operation]);

  useEffect(() => {
    if (!embedded || pendingQuickBooking || pendingAssetQuickBook || bookingsTabFocus < 1) {
      return;
    }
    if (openedForPendingCreateRef.current) {
      openedForPendingCreateRef.current = false;
      return;
    }
    showBookingsListFromTab();
  }, [embedded, bookingsTabFocus, pendingQuickBooking, pendingAssetQuickBook, showBookingsListFromTab]);

  const bookingFlowGuardActive =
    parkingVehicleOpen ||
    healthDeclarationOpen ||
    cancellationPolicyOpen ||
    paymentOpen ||
    isEditingBooking ||
    (operation === 'create' &&
      (createFlowOrigin !== null ||
        pendingQuickBooking !== null ||
        pendingAssetQuickBook !== null ||
        Boolean(createAssetId)));

  useEffect(() => {
    if (!embedded) {
      registerBookingFlowGuard(null);
      return;
    }
    registerBookingFlowGuard({
      isActive: bookingFlowGuardActive,
      confirmLeave: () => {
        if (
          parkingVehicleOpen ||
          healthDeclarationOpen ||
          cancellationPolicyOpen ||
          paymentOpen
        ) {
          return window.confirm(t('nav.leavePayment'));
        }
        if (isEditingBooking) {
          return window.confirm(t('bookings.discardEdit'));
        }
        return window.confirm(t('bookings.discardCreate'));
      },
      abandonFlow: () => {
        showBookingsListFromTab();
      },
    });
    return () => registerBookingFlowGuard(null);
  }, [
    embedded,
    bookingFlowGuardActive,
    parkingVehicleOpen,
    healthDeclarationOpen,
    cancellationPolicyOpen,
    paymentOpen,
    isEditingBooking,
    registerBookingFlowGuard,
    showBookingsListFromTab,
    t,
  ]);

  const confirmationLabels = useMemo(
    () => ({
      title: t('bookings.confirmation.title'),
      subtitle: t('bookings.confirmation.subtitle'),
      scheduleLabel: t('bookings.confirmation.schedule'),
      peopleLabel: t('bookings.confirmation.peopleLabel'),
      vehicleLabel: t('bookings.confirmation.vehicleLabel'),
      bookingIdLabel: t('bookings.confirmation.bookingId'),
      showBarcode: t('bookings.confirmation.showBarcode'),
      hideBarcode: t('bookings.confirmation.hideBarcode'),
      qrCaption: t('bookings.confirmation.qrCaption'),
      venueLoading: t('bookings.confirmation.venueLoading'),
      venueUnknown: t('bookings.confirmation.venueUnknown'),
      loading: t('bookings.confirmation.loading'),
    }),
    [t],
  );

  const onViewConfirmationFromList = useCallback(
    (row: Record<string, unknown>) => {
      const id = bookingRecordPrimaryId(row) ?? extractBookingIdFromApiPayload(row);
      if (!id) {
        return;
      }

      // Never present unpaid Pending bookings as a successful reservation.
      if (bookingRecordNeedsPayment(row)) {
        setCreateConfirmation(null);
        setAwaitingPaymentBookingId(id);
        setError(null);
        setFailureContext(null);
        return;
      }

      const scheduleText = bookingRecordIsHourly(row)
        ? bookingRecordScheduleLabel(row, language)
        : bookingRecordDateRangeLabel(row, language);
      const peopleRaw = row.amount_of_people;
      const amountOfPeople =
        typeof peopleRaw === 'number' && Number.isFinite(peopleRaw) ? peopleRaw : null;
      const nestedAsset = isPlainObject(row.asset) ? (row.asset as Record<string, unknown>) : null;
      const assetId = bookingRecordAssetId(row);
      const initialVenue = resolveBookingConfirmationVenue(
        assetId,
        [nestedAsset, row],
        language,
      );
      const baseDetails: BookingConfirmationDetails = {
        bookingId: id,
        assetName: initialVenue.assetName,
        assetAddress: initialVenue.assetAddress,
        scheduleText,
        startDateIso: typeof row.start_date === 'string' ? row.start_date : null,
        endDateIso: typeof row.end_date === 'string' ? row.end_date : null,
        amountOfPeople,
        showPeopleCount: bookingConfirmationShowsPeopleCount([nestedAsset, row]),
        venuePending: Boolean(assetId && !initialVenue.assetName),
        language,
        labels: confirmationLabels,
      };
      setAwaitingPaymentBookingId(null);
      setCreateConfirmation(baseDetails);
      setError(null);
      setFailureContext(null);

      if (!assetId) {
        return;
      }
      void (async () => {
        try {
          const assetRaw = await getAssetById(assetId);
          if (!isPlainObject(assetRaw)) {
            return;
          }
          const venue = resolveBookingConfirmationVenue(
            assetId,
            [assetRaw, nestedAsset, row],
            language,
          );
          setCreateConfirmation((prev) =>
            prev && prev.bookingId === id
              ? {
                  ...prev,
                  assetName: venue.assetName || prev.assetName,
                  assetAddress: venue.assetAddress ?? prev.assetAddress,
                  venuePending: false,
                  showPeopleCount: bookingConfirmationShowsPeopleCount([
                    assetRaw,
                    nestedAsset,
                    row,
                  ]),
                }
              : prev,
          );
        } catch {
          setCreateConfirmation((prev) =>
            prev && prev.bookingId === id ? { ...prev, venuePending: false } : prev,
          );
        }
      })();
    },
    [confirmationLabels, language],
  );

  const showConfirmedBookingCard = useCallback(
    async (bookingId: string) => {
      const id = bookingId.trim();
      if (!id) {
        return;
      }
      try {
        const raw = await fetchBookingById(id);
        const row = unwrapBookingRecord(raw);
        if (!row || !bookingRecordIsPaymentConfirmed(row)) {
          setAwaitingPaymentBookingId(id);
          setCreateConfirmation(null);
          return;
        }
        onViewConfirmationFromList(row);
      } catch {
        setAwaitingPaymentBookingId(id);
        setCreateConfirmation(null);
      }
    },
    [onViewConfirmationFromList],
  );

  const onResumePayplusForAwaitingBooking = useCallback(async () => {
    const id = awaitingPaymentBookingId?.trim();
    if (!id || awaitingPaymentBusy) {
      return;
    }
    setAwaitingPaymentBusy(true);
    setError(null);
    try {
      let returnAssetId: string | null = null;
      try {
        const raw = await fetchBookingById(id);
        const row = unwrapBookingRecord(raw);
        if (row) {
          returnAssetId = bookingRecordAssetId(row);
        }
      } catch {
        /* still open PayPlus; cancel will land on bookings */
      }
      plantPayplusBrowserBackToAsset(returnAssetId);
      const payplus = await startPayplusCheckoutForBooking(id, { returnAssetId });
      prepareUiBeforePayplusRedirect(returnAssetId);
      openPayplusPaymentPage(payplus.payment_page_link, { returnAssetId });
    } catch (err) {
      payplusNavigatingAwayRef.current = false;
      const msg = formatBookingUserError(err) || t('bookings.payplus.redirectError');
      setError(msg);
    } finally {
      if (!payplusNavigatingAwayRef.current) {
        setAwaitingPaymentBusy(false);
      }
    }
  }, [
    awaitingPaymentBookingId,
    awaitingPaymentBusy,
    formatBookingUserError,
    prepareUiBeforePayplusRedirect,
    t,
  ]);


  const onSubmit = async (e?: React.FormEvent): Promise<boolean> => {
    e?.preventDefault();
    setError(null);
    setStatusNotice(null);
    setFailureContext(null);
    setResponse(null);
    setCreateConfirmation(null);
    setLoading(true);
    try {
      let data: unknown;
      let responseOperation: Operation = operation;
      const id = bookingId.trim();

      switch (operation) {
        case 'getById':
          data = await fetchBookingById(bookingId);
          break;
        case 'listAll':
          data = await fetchBookingsList();
          break;
        case 'create': {
          if (sessionCannotCreateBookings && !editingBookingId) {
            throw new Error(
              ownerCannotBook
                ? t('bookings.error.roleBlock.assetOwner')
                : t('bookings.error.roleNotAllowed'),
            );
          }
          let workingCreateBody = createBody;
          if (!editingBookingId && createFlowOrigin === 'assets' && effectiveQuickBookingMode) {
            const startIso =
              typeof createBody.start_date === 'string' ? createBody.start_date.trim() : '';
            const endIso = typeof createBody.end_date === 'string' ? createBody.end_date.trim() : '';
            if (startIso && endIso) {
              const refreshed = refreshStaleQuickBookingSchedule(
                startIso,
                endIso,
                effectiveQuickBookingMode,
              );
              if (
                refreshed &&
                (refreshed.startIso !== startIso || refreshed.endIso !== endIso)
              ) {
                workingCreateBody = {
                  ...createBody,
                  start_date: refreshed.startIso,
                  end_date: refreshed.endIso,
                };
                setCreateBody(workingCreateBody);
              }
            }
          }
          if (usePeriodBasedScheduling && selectedBookingOption?.kind) {
            workingCreateBody = applyPeriodCountEndDate(workingCreateBody);
          }
          const createPayload = normalizeBookingCreatePayload(workingCreateBody);
          const addonRows = Array.isArray(createPayload.addons)
            ? (createPayload.addons as Array<{ addon_id: number; quantity: number }>)
            : [];
          if (!editingBookingId && createAssetId && addonRows.length > 0) {
            const catalogErr = validateBookingAddonsInCatalog(
              addonRows,
              addonsCatalog,
              bookingAddonChoices,
            );
            if (catalogErr) {
              throw new Error(catalogErr);
            }
            if (bookingAddonChoices.length > 0) {
              const addonErr = validateBookingAddonQuantities(
                addonRows,
                bookingAddonChoices,
                addonsCatalog,
              );
              if (addonErr) {
                throw new Error(addonErr);
              }
            }
          }
          const updatePayloadBody = (() => {
            const body = { ...createPayload };
            delete body.addons;
            return body;
          })();
          const bookingUpdateOpts = editingBookingOriginalEndDate
            ? { originalEndDate: editingBookingOriginalEndDate }
            : undefined;
          if (editingBookingId) {
            const startIso =
              typeof createBody.start_date === 'string' ? createBody.start_date : '';
            const endIso =
              typeof createPayload.end_date === 'string' ? createPayload.end_date : '';
            const updateScheduleErr =
              usePeriodBasedScheduling && selectedBookingOption?.kind
                ? validateBookingUpdatePeriodSchedule(
                    startIso,
                    endIso,
                    selectedBookingOption.kind,
                    editMinPeriodCount,
                  )
                : validateBookingUpdateEndDate(startIso, endIso);
            if (updateScheduleErr) {
              throw new Error(updateScheduleErr);
            }
            const updatePayload = normalizeBookingUpdatePayload(updatePayloadBody, bookingUpdateOpts);
            if (typeof updatePayload.total_price !== 'number') {
              throw new Error(
                t('bookings.couldNotCalculateTotal'),
              );
            }
          } else if (!editingBookingId) {
            const startIso =
              typeof createPayload.start_date === 'string' ? createPayload.start_date : '';
            const endIso = typeof createPayload.end_date === 'string' ? createPayload.end_date : '';
            const orderErr = validateBookingCreateOrderReady({
              startIso,
              endIso,
              createAssetId: createAssetId || null,
              totalPrice:
                quickBookingLockedTotal !== null
                  ? quickBookingLockedTotal
                  : createPayload.total_price,
              addonRows,
              addonChoices: bookingAddonChoices,
              addonsCatalog,
              hourly: createScheduleValidation.hourly,
              sameDayHourly: createScheduleValidation.sameDayHourly,
              periodKind: createScheduleValidation.periodKind,
              assetReservedDayKeys,
              couldNotCalculateTotalMessage: t('bookings.couldNotCalculateTotal'),
              rangeBlockedMessage: t('bookings.calendar.rangeBlocked'),
            });
            if (orderErr) {
              throw new Error(orderErr);
            }
            if (createAssetId) {
              const snapshot = createBookingAsset ?? lastBookedAssetSnapshotRef.current;
              await resolveAssetForBookingCreate(createAssetId, snapshot);
            }
          }
          if (editingBookingId) {
            await updateBooking(
              editingBookingId,
              normalizeBookingUpdatePayload(updatePayloadBody, bookingUpdateOpts),
            );
            setEditingBookingId(null);
            setEditingBookingOriginalEndDate(null);
            setEditingBookingReservedAddonQty(new Map());
            setOperation('listAll');
            responseOperation = 'listAll';
            data = await fetchBookingsList();
          } else {
            const startIso =
              typeof createPayload.start_date === 'string' ? createPayload.start_date : '';
            const endIso =
              typeof createPayload.end_date === 'string' ? createPayload.end_date : '';
            // Schedule, reservation and add-on checks above have all passed. Collect payment
            // before creating the booking; the payment step re-invokes onSubmit on success.
            // Only charge when there is a positive total — free bookings skip the payment page.
            const bookingSubtotal = resolveBookingCheckoutSubtotal(
              quickBookingLockedTotal,
              computedBookingTotalPrice,
              createPayload.total_price,
            );
            const hasPayableTotal = bookingSubtotal !== null;
            const { payableTotal, amountLabel } = bookingCheckoutAmount(
              hasPayableTotal ? bookingSubtotal : null,
              bookingCurrency,
            );
            const assetForHealth = createBookingAsset ?? lastBookedAssetSnapshotRef.current;
            if (
              !paymentCompletedRef.current &&
              assetForHealth &&
              assetIsParkingBooking(assetForHealth) &&
              !parkingVehicleIdRef.current
            ) {
              pendingCheckoutPayableRef.current = payableTotal;
              setPendingPaymentAmount(amountLabel);
              setParkingVehicleOpen(true);
              setHealthDeclarationOpen(false);
              setCancellationPolicyOpen(false);
              setPaymentOpen(false);
              return false;
            }
            if (
              !paymentCompletedRef.current &&
              assetForHealth &&
              assetIsGymBooking(assetForHealth) &&
              !healthDeclarationSatisfiedRef.current
            ) {
              let needsDeclaration = true;
              try {
                const completed = await fetchMedicalQuestionnaireCompleted();
                if (completed) {
                  healthDeclarationSatisfiedRef.current = true;
                  needsDeclaration = false;
                }
              } catch {
                needsDeclaration = true;
              }
              if (needsDeclaration) {
                pendingCheckoutPayableRef.current = payableTotal;
                setPendingPaymentAmount(amountLabel);
                setHealthDeclarationOpen(true);
                setParkingVehicleOpen(false);
                setCancellationPolicyOpen(false);
                setPaymentOpen(false);
                return false;
              }
            }
            if (hasPayableTotal && !paymentCompletedRef.current) {
              pendingCheckoutPayableRef.current = payableTotal;
              setPendingPaymentAmount(amountLabel);
              setCancellationPolicyOpen(true);
              setPaymentOpen(false);
              return false;
            }
            const wireTotalForCreate = (() => {
              if (payplusGatewayCheckoutRef.current && payplusTestChargeRef.current) {
                console.info(
                  `[PayPlus test] Creating booking with total_price=${PAYPLUS_TEST_CHARGE_NIS} NIS (sandbox test card).`,
                );
                setStatusNotice(t('bookings.payplus.testChargeNotice'));
                return PAYPLUS_TEST_CHARGE_NIS;
              }
              if (hasPayableTotal && paymentCompletedRef.current) {
                return applyBookingVat(bookingSubtotal as number);
              }
              return createPayload.total_price;
            })();
            const pendingCreateAddons = addonRows;
            const creditWire = bookingCreditsPaymentRef.current;
            const parkingVehicleId = parkingVehicleIdRef.current;
            const assetForParking =
              createBookingAsset ?? lastBookedAssetSnapshotRef.current;
            const isParkingCreate =
              Boolean(assetForParking && assetIsParkingBooking(assetForParking));
            const createResponse = await createBooking({
              ...bookingCreateBodyWithoutAddons({
                ...createPayload,
                total_price: wireTotalForCreate,
                amount_of_people: isParkingCreate ? 1 : createPayload.amount_of_people,
                ...(creditWire ?? {}),
                ...(parkingVehicleId ? { vehicle_id: parkingVehicleId } : {}),
              }),
              booking_date: new Date().toISOString(),
            });
            // Capture before any create-flow teardown — needed for PayPlus Back/cancel → asset.
            const payplusReturnAssetId =
              (typeof createPayload.asset_id === 'string' && createPayload.asset_id.trim()) ||
              createFlowReturnAssetIdRef.current?.trim() ||
              null;

            let createdId = extractBookingIdFromApiPayload(createResponse);

            // Stage B PayPlus: do not flip to My Bookings list before redirect (that flash
            // also becomes the history entry users hit when pressing Back from PayPlus).
            if (payplusGatewayCheckoutRef.current) {
              plantPayplusBrowserBackToAsset(payplusReturnAssetId);

              if (!createdId) {
                const listForId = await fetchBookingsList();
                createdId =
                  recoverCreatedBookingId(normalizeBookingListRecords(listForId), {
                    assetId: createAssetId,
                    startIso,
                    endIso,
                  }) ?? null;
              }

              if (createdId && pendingCreateAddons.length > 0) {
                await updateBooking(
                  createdId,
                  normalizeBookingUpdatePayload({
                    addons: pendingCreateAddons,
                    total_price: wireTotalForCreate,
                  }),
                );
              }
              if (createdId && selectedPricingTypeId !== null) {
                saveBookingPricingTypePreference(createdId, selectedPricingTypeId);
              }

              if (createdId) {
                payplusGatewayCheckoutRef.current = false;
                payplusRetryBookingIdRef.current = createdId;
                try {
                  const payplus = await startPayplusCheckoutForBooking(createdId, {
                    returnAssetId: payplusReturnAssetId,
                  });
                  payplusRetryBookingIdRef.current = null;
                  bookingCreditsPaymentRef.current = null;
                  parkingVehicleIdRef.current = null;
                  parkingVehicleSummaryRef.current = null;
                  payplusTestChargeRef.current = false;
                  // Close payment sheet + show asset under history before leaving (Back ≠ stuck Processing).
                  prepareUiBeforePayplusRedirect(payplusReturnAssetId);
                  openPayplusPaymentPage(payplus.payment_page_link, {
                    returnAssetId: payplusReturnAssetId,
                  });
                  return true;
                } catch (payplusErr) {
                  payplusNavigatingAwayRef.current = false;
                  paymentFailureMessageRef.current =
                    formatBookingUserError(payplusErr) || t('bookings.payplus.redirectError');
                  setError(paymentFailureMessageRef.current);
                  setCreateConfirmation(null);
                  setAwaitingPaymentBookingId(createdId);
                  setPaymentOpen(false);
                  createFlowReturnAssetIdRef.current = null;
                  setCreateFlowOrigin(null);
                  setQuickBookingMode(null);
                  setQuickBookingLockedTotal(null);
                  quickBookingUsesAssetSnapshotRef.current = false;
                  setEditingBookingId(null);
                  setEditingBookingOriginalEndDate(null);
                  setEditingBookingReservedAddonQty(new Map());
                  setOperation('listAll');
                  responseOperation = 'listAll';
                  data = await fetchBookingsList();
                  throw payplusErr instanceof Error
                    ? payplusErr
                    : new Error(paymentFailureMessageRef.current);
                }
              }
              // No booking id — fall through to normal post-create handling.
              payplusGatewayCheckoutRef.current = false;
            }

            createFlowReturnAssetIdRef.current = null;
            setCreateFlowOrigin(null);
            setQuickBookingMode(null);
            setQuickBookingLockedTotal(null);
            quickBookingUsesAssetSnapshotRef.current = false;
            setEditingBookingId(null);
            setEditingBookingOriginalEndDate(null);
            setEditingBookingReservedAddonQty(new Map());
            setOperation('listAll');
            responseOperation = 'listAll';
            data = await fetchBookingsList();

            // The create response does not always echo the new id in a readable shape; recover it
            // from the refreshed list so the confirmation (and its QR) still renders.
            createdId =
              createdId ??
              recoverCreatedBookingId(normalizeBookingListRecords(data), {
                assetId: createAssetId,
                startIso,
                endIso,
              });

            // POST `/bookings/create` does not accept add-on lines on this API — attach via PATCH.
            if (createdId && pendingCreateAddons.length > 0) {
              await updateBooking(
                createdId,
                normalizeBookingUpdatePayload({
                  addons: pendingCreateAddons,
                  total_price: wireTotalForCreate,
                }),
              );
              data = await fetchBookingsList();
            }
            if (createdId && selectedPricingTypeId !== null) {
              saveBookingPricingTypePreference(createdId, selectedPricingTypeId);
            }

            if (createdId) {
              // Card/PayPlus path must not show “reservation successful” until status is Confirmed.
              // Credits / free bookings may confirm immediately.
              if (hasPayableTotal && !creditWire) {
                setCreateConfirmation(null);
                setAwaitingPaymentBookingId(createdId);
                lastBookedAssetSnapshotRef.current = null;
                parkingVehicleIdRef.current = null;
                parkingVehicleSummaryRef.current = null;
                break;
              }

              const bookedSnapshot = lastBookedAssetSnapshotRef.current;
              let assetForConfirmation = bookedSnapshot ?? createBookingAsset;
              if (!assetForConfirmation && createAssetId) {
                try {
                  const assetRaw = await getAssetById(createAssetId);
                  if (isPlainObject(assetRaw)) {
                    assetForConfirmation = assetRaw;
                  }
                } catch {
                  /* confirmation still shown without address */
                }
              }
              const listRecords = normalizeBookingListRecords(data);
              const createdRow = listRecords.find(
                (row) => bookingRecordPrimaryId(row) === createdId,
              );
              const confirmStartIso =
                createdRow && typeof createdRow.start_date === 'string'
                  ? createdRow.start_date
                  : startIso;
              const confirmEndIso =
                createdRow && typeof createdRow.end_date === 'string'
                  ? createdRow.end_date
                  : endIso;
              const scheduleBooking: Record<string, unknown> = {
                start_date: confirmStartIso,
                end_date: confirmEndIso,
              };
              if (isHourlySameDayBooking) {
                scheduleBooking.pricing_type_name = 'hourly';
              } else if (selectedBookingOption?.label) {
                scheduleBooking.pricing_type_name = selectedBookingOption.label;
              }
              const scheduleText = bookingRecordIsHourly(scheduleBooking)
                ? bookingRecordScheduleLabel(scheduleBooking, language)
                : bookingRecordDateRangeLabel(scheduleBooking, language);
              const peopleRaw = createPayload.amount_of_people;
              const amountOfPeople =
                typeof peopleRaw === 'number' && Number.isFinite(peopleRaw) ? peopleRaw : null;
              const venue = resolveBookingConfirmationVenue(
                createAssetId,
                [
                  assetForConfirmation,
                  bookedSnapshot,
                  createBookingAsset,
                  {
                    asset_name:
                      typeof createPayload.asset_name === 'string'
                        ? createPayload.asset_name
                        : undefined,
                  },
                ],
                language,
              );
              const confirmationDetails: BookingConfirmationDetails = {
                bookingId: createdId,
                assetName: venue.assetName,
                assetAddress: venue.assetAddress,
                scheduleText,
                startDateIso: confirmStartIso,
                endDateIso: confirmEndIso,
                amountOfPeople,
                showPeopleCount: bookingConfirmationShowsPeopleCount([
                  assetForConfirmation,
                  bookedSnapshot,
                  createBookingAsset,
                ]),
                vehicleSummary: parkingVehicleSummaryRef.current,
                venuePending: Boolean(createAssetId && !venue.assetName),
                language,
                labels: confirmationLabels,
              };
              setCreateConfirmation(confirmationDetails);
              setAwaitingPaymentBookingId(null);
              lastBookedAssetSnapshotRef.current = null;
              parkingVehicleIdRef.current = null;
              parkingVehicleSummaryRef.current = null;
              if (createAssetId) {
                void (async () => {
                  try {
                    const assetRaw = await getAssetById(createAssetId);
                    if (!isPlainObject(assetRaw)) {
                      return;
                    }
                    const enrichedVenue = resolveBookingConfirmationVenue(
                      createAssetId,
                      [assetRaw, assetForConfirmation, bookedSnapshot, createBookingAsset],
                      language,
                    );
                    setCreateConfirmation((prev) =>
                      prev && prev.bookingId === createdId
                        ? {
                            ...prev,
                            assetName: enrichedVenue.assetName || prev.assetName,
                            assetAddress: enrichedVenue.assetAddress ?? prev.assetAddress,
                            venuePending: false,
                            showPeopleCount: bookingConfirmationShowsPeopleCount([
                              assetRaw,
                              assetForConfirmation,
                              bookedSnapshot,
                              createBookingAsset,
                            ]),
                          }
                        : prev,
                    );
                  } catch {
                    setCreateConfirmation((prev) =>
                      prev && prev.bookingId === createdId
                        ? { ...prev, venuePending: false }
                        : prev,
                    );
                  }
                })();
              }
            }
          }
          break;
        }
        case 'modifyPrice': {
          if (!id) {
            throw new Error('Booking ID is required.');
          }
          const rawPrice = modifyPriceInput.trim().replace(',', '.');
          if (rawPrice === '') {
            throw new Error('Enter a total price.');
          }
          const n = Number(rawPrice);
          if (!Number.isFinite(n)) {
            throw new Error('Total price must be a valid number.');
          }
          data = await modifyBookingPrice(id, n);
          break;
        }
        case 'cancelClient': {
          const cancelId = bookingId.trim();
          if (!cancelId) {
            throw new Error('Booking ID is required.');
          }
          await completeClientBookingCancel(cancelId);
          data = await fetchBookingsList();
          data = filterDismissedBookingsFromList(data);
          responseOperation = 'listAll';
          break;
        }
        case 'cancelOwner':
          data = await cancelBookingAsOwner(bookingId, ownerCancelAnswer);
          break;
        case 'escalate':
          data = await escalateBooking(bookingId);
          break;
        default: {
          const _n: never = operation;
          throw new Error(String(_n));
        }
      }

      if (operation === 'create' && responseOperation === 'create') {
        setCreateFlowOrigin(null);
      }
      setResponse({ operation: responseOperation, data });
      if (operation === 'create' && responseOperation === 'listAll') {
        if (!payplusNavigatingAwayRef.current) {
          setPaymentOpen(false);
          setCancellationPolicyOpen(false);
          setHealthDeclarationOpen(false);
          setPendingPaymentAmount(null);
          bookingCreditsPaymentRef.current = null;
          createFlowReturnAssetIdRef.current = null;
        }
      }
      paymentCompletedRef.current = false;
      return true;
    } catch (err) {
      paymentCompletedRef.current = false;
      if (operation === 'listAll') {
        setResponse({ operation: 'listAll', data: [] });
        return true;
      }
      const failureCtx: BookingFailureContext = {
        operation,
        bookingId: bookingId.trim(),
        editingBookingId: operation === 'create' ? editingBookingId : undefined,
        modifyPriceInput: operation === 'modifyPrice' ? modifyPriceInput.trim() : undefined,
        ownerCancelAnswer: operation === 'cancelOwner' ? ownerCancelAnswer : undefined,
      };
      setFailureContext(failureCtx);
      const userMessage = formatBookingUserError(err);
      paymentFailureMessageRef.current = userMessage;
      if (isIncompleteRegistrationBookingError(err)) {
        setError(userMessage);
        holdCheckoutForRegistrationRef.current = true;
        paymentCompletedRef.current = true;
        promptProfileCompletion(() => {
          holdCheckoutForRegistrationRef.current = false;
          paymentCompletedRef.current = true;
          void onSubmitRef.current();
        });
      } else if (err instanceof AssetRequestError) {
        setError(err);
      } else {
        setError(userMessage);
      }
      reportBookingFailureToApp(
        reportError,
        failureCtx,
        err,
        operation === 'create' ? { createBody } : undefined,
      );
      return false;
    } finally {
      if (!payplusNavigatingAwayRef.current) {
        setLoading(false);
      }
    }
  };
  onSubmitRef.current = onSubmit;

  // Plain function (not memoized) so it always closes over the latest onSubmit/state.
  const onPaymentConfirmed = async (
    options?: BookingPaymentPayOptions,
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    bookingCreditsPaymentRef.current = null;
    paymentFailureMessageRef.current = null;
    payplusNavigatingAwayRef.current = false;
    payplusTestChargeRef.current = Boolean(options?.payplusTestCard);

    const retryBookingId = payplusRetryBookingIdRef.current?.trim() || null;
    if (retryBookingId) {
      try {
        setLoading(true);
        const returnAssetId =
          (typeof createBody.asset_id === 'string' && createBody.asset_id.trim()) ||
          createFlowReturnAssetIdRef.current?.trim() ||
          null;
        plantPayplusBrowserBackToAsset(returnAssetId);
        const payplus = await startPayplusCheckoutForBooking(retryBookingId, {
          returnAssetId,
        });
        payplusRetryBookingIdRef.current = null;
        prepareUiBeforePayplusRedirect(returnAssetId);
        openPayplusPaymentPage(payplus.payment_page_link, { returnAssetId });
        return { success: true };
      } catch (payplusErr) {
        payplusNavigatingAwayRef.current = false;
        const msg =
          formatBookingUserError(payplusErr) || t('bookings.payplus.redirectError');
        paymentFailureMessageRef.current = msg;
        setError(msg);
        return { success: false, errorMessage: msg };
      } finally {
        if (!payplusNavigatingAwayRef.current) {
          setLoading(false);
        }
      }
    }

    payplusGatewayCheckoutRef.current = true;
    paymentCompletedRef.current = true;
    const ok = await onSubmit();
    if (ok) {
      if (!payplusNavigatingAwayRef.current) {
        setPaymentOpen(false);
        setPendingPaymentAmount(null);
      }
      return { success: true };
    }
    payplusGatewayCheckoutRef.current = false;
    payplusTestChargeRef.current = false;
    if (!holdCheckoutForRegistrationRef.current) {
      paymentCompletedRef.current = false;
    }
    return {
      success: false,
      errorMessage: paymentFailureMessageRef.current ?? undefined,
    };
  };

  const onWorkerCreditsConfirmed = async (
    input: { nationalId: string; businessId: string },
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    bookingCreditsPaymentRef.current = normalizeWorkerCreditsPaymentWire(input);
    paymentCompletedRef.current = true;
    paymentFailureMessageRef.current = null;
    const ok = await onSubmit();
    if (ok) {
      setPaymentOpen(false);
      setPendingPaymentAmount(null);
      return { success: true };
    }
    if (!holdCheckoutForRegistrationRef.current) {
      bookingCreditsPaymentRef.current = null;
      paymentCompletedRef.current = false;
    }
    return {
      success: false,
      errorMessage: paymentFailureMessageRef.current ?? undefined,
    };
  };

  const onPrivateClientWalletCreditsConfirmed = async (): Promise<{
    success: boolean;
    errorMessage?: string;
  }> => {
    bookingCreditsPaymentRef.current = normalizePrivateClientPayWithCreditsWire();
    paymentCompletedRef.current = true;
    paymentFailureMessageRef.current = null;
    const ok = await onSubmit();
    if (ok) {
      setPaymentOpen(false);
      setPendingPaymentAmount(null);
      return { success: true };
    }
    if (!holdCheckoutForRegistrationRef.current) {
      bookingCreditsPaymentRef.current = null;
      paymentCompletedRef.current = false;
    }
    return {
      success: false,
      errorMessage: paymentFailureMessageRef.current ?? undefined,
    };
  };

  const onPaymentCancelled = () => {
    paymentCompletedRef.current = false;
    payplusGatewayCheckoutRef.current = false;
    payplusRetryBookingIdRef.current = null;
    payplusTestChargeRef.current = false;
    payplusNavigatingAwayRef.current = false;
    bookingCreditsPaymentRef.current = null;
    holdCheckoutForRegistrationRef.current = false;
    setLoading(false);
    setPaymentOpen(false);
    setCancellationPolicyOpen(false);
    setHealthDeclarationOpen(false);
    setParkingVehicleOpen(false);
    parkingVehicleIdRef.current = null;
    parkingVehicleSummaryRef.current = null;
    setPendingPaymentAmount(null);
    setPaymentCheckoutView(businessWorkerCheckout ? 'worker-credits' : 'regular');
    if (createFlowOrigin === 'assets') {
      exitCreateFlowToOrigin();
    }
  };

  const onParkingVehicleChosen = useCallback(
    (vehicle: ProfileVehicle) => {
      parkingVehicleIdRef.current = vehicle.id;
      parkingVehicleSummaryRef.current = formatProfileVehicleSummaryLine(vehicle);
      setParkingVehicleOpen(false);
      const asset = createBookingAsset ?? lastBookedAssetSnapshotRef.current;
      const payable = pendingCheckoutPayableRef.current;
      const amountLabel =
        typeof payable === 'number' && payable > 0
          ? formatMoneyAmount(
              payable,
              asset ? resolveAssetLocaleCurrency(asset) : DEFAULT_ASSET_LOCALE_CURRENCY,
            )
          : pendingPaymentAmount;
      void tryOpenCheckoutForGymBooking(asset, payable, amountLabel);
    },
    [createBookingAsset, pendingPaymentAmount, tryOpenCheckoutForGymBooking],
  );

  const onHealthDeclarationSubmitted = useCallback(
    async (_payload: GymHealthDeclarationWirePayload) => {
      await markMedicalQuestionnaireCompleted();
      healthDeclarationSatisfiedRef.current = true;
      const payable = pendingCheckoutPayableRef.current;
      const amountLabel =
        typeof payable === 'number' && payable > 0
          ? formatMoneyAmount(
              payable,
              createBookingAsset
                ? resolveAssetLocaleCurrency(createBookingAsset)
                : DEFAULT_ASSET_LOCALE_CURRENCY,
            )
          : null;
      proceedToCheckoutSteps(payable, amountLabel);
    },
    [createBookingAsset, proceedToCheckoutSteps],
  );

  const onCancellationPolicyCancelled = () => {
    onPaymentCancelled();
  };

  const checkoutStepOpen =
    parkingVehicleOpen || healthDeclarationOpen || cancellationPolicyOpen || paymentOpen;

  const showCreateFlowListChrome = operation === 'create' && createFlowOrigin !== null;
  const showEditBookingChrome = isEditingBooking && createFlowOrigin === null;
  const isBrowsePage = embedded && pageTheme;
  const displayedBookingAssetPreview = useMemo(() => {
    if (!bookingAssetPreview || !canRenderAssetGetByIdView(bookingAssetPreview)) {
      return null;
    }
    return enrichAssetRecordAddressForDisplay(bookingAssetPreview);
  }, [bookingAssetPreview]);
  const showBookingListAssetPreview = Boolean(
    bookingAssetPreviewLoading || bookingAssetPreview || bookingAssetPreviewError,
  );
  /** New bookings from the asset page use quick booking + payment only (no legacy create form). */
  const hideAssetsBrowseCreateForm =
    isBrowsePage && createFlowOrigin === 'assets' && !isEditingBooking;
  const showAssetQuickBookPanel =
    createBookingAsset !== null &&
    operation === 'create' &&
    createFlowOrigin === 'assets' &&
    !isEditingBooking &&
    !checkoutStepOpen &&
    quickBookingLockedTotal === null;

  useEffect(() => {
    if (!showAssetQuickBookPanel) {
      return;
    }
    return scheduleScrollPanelToElement(panelScrollRef, quickBookPanelRef);
  }, [showAssetQuickBookPanel, createAssetId, formEpoch]);
  const createFlowBackLabel =
    createFlowOrigin === 'assets' ? t('bookings.backToAssetsLabel') : t('bookings.backToBookingsLabel');
  const createFlowCancelLabel = editingBookingId
    ? t('bookings.cancelEdit')
    : t('bookings.cancelBooking');
  const showBrowseAssetCreateError =
    Boolean(error) && hideAssetsBrowseCreateForm && !checkoutStepOpen;
  const showAssetCreateFlowEmpty =
    hideAssetsBrowseCreateForm &&
    !checkoutStepOpen &&
    quickBookingLockedTotal === null &&
    !showAssetQuickBookPanel &&
    !showBrowseAssetCreateError;

  const panelTitle =
    editingBookingId
      ? t('bookings.titleUpdate')
      : createAssetId && operation === 'create'
        ? t('bookings.titleCreate')
        : t('bookings.title');
  const hideBrowseBookingsListHeader =
    isBrowsePage &&
    !editingBookingId &&
    !(createAssetId && operation === 'create') &&
    !showBookingListAssetPreview &&
    !showCreateFlowListChrome &&
    !showEditBookingChrome;

  /** PC: the bookings list uses the wide multi-column page; flows stay page-width. */
  const useBrowseWideBookingsList =
    isBrowsePage &&
    response?.operation === 'listAll' &&
    !editingBookingId &&
    operation !== 'create' &&
    !showBookingListAssetPreview;

  const localizedPricingLabel = selectedBookingOption?.label
    ? localizeCatalogDisplayName(selectedBookingOption.label, language, 'pricing')
    : '';

  const bookingCurrency = useMemo(
    () =>
      createBookingAsset !== null
        ? resolveAssetLocaleCurrency(createBookingAsset)
        : DEFAULT_ASSET_LOCALE_CURRENCY,
    [createBookingAsset],
  );

  const bookingCheckoutSubtotal = useMemo(() => {
    if (operation !== 'create' || editingBookingId) {
      return null;
    }
    return resolveBookingCheckoutSubtotal(
      quickBookingLockedTotal,
      computedBookingTotalPrice,
      createBody.total_price,
    );
  }, [
    operation,
    editingBookingId,
    quickBookingLockedTotal,
    computedBookingTotalPrice,
    createBody.total_price,
  ]);

  /** Always derive VAT-inclusive label when payment is open (guards stale ex-VAT state). */
  const paymentDisplayAmountLabel = useMemo(() => {
    const { amountLabel } = bookingCheckoutAmount(bookingCheckoutSubtotal, bookingCurrency);
    if (paymentOpen && amountLabel !== null) {
      return amountLabel;
    }
    return pendingPaymentAmount;
  }, [
    paymentOpen,
    bookingCheckoutSubtotal,
    bookingCurrency,
    pendingPaymentAmount,
  ]);

  useEffect(() => {
    if (!paymentOpen || !privateClientCheckout) {
      return;
    }
    let cancelled = false;
    const loginId = getStoredLoginIdentifier();
    const stored = getStoredProfileWalletCredits(loginId);
    if (stored !== null && !cancelled) {
      setWalletCreditsBalanceLabel(
        stored.toLocaleString(language === 'he' ? 'he-IL' : 'en-US', { maximumFractionDigits: 0 }),
      );
    }
    void (async () => {
      try {
        const data = await fetchProfile();
        if (cancelled) {
          return;
        }
        const credits = resolvePrivateClientCreditsFromProfilePayload(data, loginId);
        setWalletCreditsBalanceLabel(
          credits.toLocaleString(language === 'he' ? 'he-IL' : 'en-US', { maximumFractionDigits: 0 }),
        );
      } catch {
        /* keep stored label */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [paymentOpen, privateClientCheckout, language]);

  const onCancellationPolicyAccepted = useCallback(() => {
    const { payableTotal, amountLabel } = bookingCheckoutAmount(
      bookingCheckoutSubtotal,
      bookingCurrency,
    );
    pendingCheckoutPayableRef.current = payableTotal;
    if (amountLabel !== null) {
      setPendingPaymentAmount(amountLabel);
    }

    // Reservation conflicts can happen between choosing a range and reaching payment.
    // Re-check before starting the payment stage so the user isn't surprised.
    const startKey = typeof createBody.start_date === 'string' ? isoToCalendarDayKey(createBody.start_date.trim()) : null;
    const endKey = typeof createBody.end_date === 'string' ? isoToCalendarDayKey(createBody.end_date.trim()) : null;
    if (startKey && endKey) {
      const { start, end } = normalizeCalendarRangeKeys(startKey, endKey);
      if (rangeIncludesReservedDay(start, end, assetReservedDayKeys)) {
        setStatusNotice(t('bookings.calendar.rangeBlocked'));
        return;
      }
    }

    setCancellationPolicyOpen(false);
    setPaymentCheckoutView(businessWorkerCheckout ? 'worker-credits' : 'regular');
    setPaymentOpen(true);
  }, [
    bookingCheckoutSubtotal,
    bookingCurrency,
    businessWorkerCheckout,
    createBody.start_date,
    createBody.end_date,
    assetReservedDayKeys,
    t,
  ]);

  const onOpenRegularPaymentFromCredits = useCallback(() => {
    bookingCreditsPaymentRef.current = null;
    paymentCompletedRef.current = false;
    const { payableTotal, amountLabel } = bookingCheckoutAmount(
      bookingCheckoutSubtotal,
      bookingCurrency,
    );
    pendingCheckoutPayableRef.current = payableTotal;
    if (amountLabel !== null) {
      setPendingPaymentAmount(amountLabel);
    }
    setPaymentCheckoutView('regular');
  }, [bookingCheckoutSubtotal, bookingCurrency]);

  return (
    <div
      className={[
        styles.wrap,
        open && !embedded ? styles.wrapOpen : '',
        embedded ? pageThemeStyles.wrapEmbedded : '',
        pageTheme ? pageThemeStyles.wrapThemed : '',
      ]
        .filter(Boolean)
        .join(' ')}
      ref={wrapRef}
    >
      {!embedded ? (
        <button
          type="button"
          className={styles.menuButton}
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-haspopup="true"
          onClick={() => setOpen((v) => !v)}
        >
          {t('bookings.title')}
        </button>
      ) : null}
      {open || embedded ? (
        <div
          id={menuId}
          ref={panelScrollRef}
          className={[
            styles.panel,
            useWidePanel && !isBrowsePage ? styles.panelWide : '',
            embedded ? pageThemeStyles.panelEmbedded : '',
            isBrowsePage ? pageThemeStyles.browsePage : '',
            useBrowseWideBookingsList ? pageThemeStyles.browsePageWide : '',
          ]
            .filter(Boolean)
            .join(' ')}
          data-page-theme={isBrowsePage ? 'light' : undefined}
          role="region"
          aria-label={t('bookings.apiLabel')}
        >
          {hideBrowseBookingsListHeader ? null : (
          <div className={isBrowsePage ? pageThemeStyles.pageHeader : styles.panelHeader}>
            <span className={isBrowsePage ? pageThemeStyles.pageTitle : styles.panelTitle}>
              {panelTitle}
            </span>
            {!showBookingListAssetPreview ? (
              <button
                type="button"
                className={isBrowsePage ? pageThemeStyles.pageClearButton : styles.panelClearButton}
                onClick={onClearButtonClick}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={loading}
                title={t('common.emptyAll')}
              >
                {t('common.clear')}
              </button>
            ) : null}
          </div>
          )}
          {showCreateFlowListChrome && !checkoutStepOpen ? (
            <div
              className={isBrowsePage ? pageThemeStyles.browseToolbar : styles.createFlowToolbar}
              role="toolbar"
              aria-label={t('bookings.formNav')}
            >
              <button
                type="button"
                className={isBrowsePage ? pageThemeStyles.browseBackButton : styles.createFlowBackButton}
                onClick={onBackFromListCreateFlow}
                disabled={loading}
              >
                {createFlowBackLabel}
              </button>
              <button
                type="button"
                className={
                  isBrowsePage ? pageThemeStyles.browseCancelButton : styles.createFlowCancelButton
                }
                onClick={onCancelFromListCreateFlow}
                disabled={loading}
              >
                {createFlowCancelLabel}
              </button>
            </div>
          ) : null}
          {showEditBookingChrome && !checkoutStepOpen ? (
            <div
              className={isBrowsePage ? pageThemeStyles.browseToolbar : styles.createFlowToolbar}
              role="toolbar"
              aria-label={t('bookings.formNav')}
            >
              <button
                type="button"
                className={
                  isBrowsePage ? pageThemeStyles.browseCancelButton : styles.createFlowCancelButton
                }
                onClick={onCancelBookingEdit}
                disabled={loading}
              >
                {t('bookings.cancelEdit')}
              </button>
            </div>
          ) : null}
          {showBrowseAssetCreateError ? (
            <div ref={bookingErrorRef} tabIndex={-1} className={styles.bookingErrorBlock} role="alert">
              <p className={styles.bookingErrorPrimary}>{formatBookingUserError(error)}</p>
            </div>
          ) : null}
          {showAssetCreateFlowEmpty ? (
            <p className={styles.bookingOptionsStatus} role="status">
              {assetPricingStatus === 'loading' || pendingAssetQuickBook || !createAssetId
                ? t('bookings.loadingOptions')
                : t('bookings.error.assetNotBookable')}
            </p>
          ) : null}
          {showAssetQuickBookPanel && createBookingAsset ? (
            <div ref={quickBookPanelRef} tabIndex={-1}>
              <AssetQuickBookingPanel
                asset={createBookingAsset}
                currency={bookingCurrency}
                onProceed={onAssetQuickBookProceed}
                onCancel={onAssetQuickBookCancel}
                initialDayKey={assetsBrowseParams.whenDate?.trim() || null}
                initialStartTime={
                  assetsBrowseParams.whenTime
                    ? floorAssistantTimeToHour(assetsBrowseParams.whenTime)
                    : null
                }
              />
            </div>
          ) : null}
          <form
            onSubmit={onSubmit}
            autoComplete="off"
            className={isBrowsePage ? pageThemeStyles.browseForm : undefined}
          >
            {!isBrowsePage ? (
              <>
                <label className={styles.label} htmlFor={`${menuId}-operation`}>
                  {t('common.operation')}
                </label>
                <select
                  id={`${menuId}-operation`}
                  className={styles.operationSelect}
                  value={operation}
                  onChange={(ev) => onOperationChange(ev.target.value as Operation)}
                >
              {bookingOperationChoices.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
                </select>
              </>
            ) : null}

            {operationNeedsBookingId(operation) ? (
              <>
                <label className={styles.label} htmlFor={`${menuId}-booking-id`} style={{ marginTop: '0.65rem' }}>
                  Booking ID
                </label>
                <div className={styles.inputRow}>
                  <input
                    key={`${menuId}-booking-id-${formEpoch}`}
                    id={`${menuId}-booking-id`}
                    className={styles.input}
                    name="bookingId"
                    value={bookingId}
                    onChange={(ev) => setBookingId(ev.target.value)}
                    placeholder="UUID"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
              </>
            ) : null}

            {operation === 'modifyPrice' ? (
              <>
                <label className={styles.label} htmlFor={`${menuId}-modify-price`} style={{ marginTop: '0.5rem' }}>
                  New total price
                </label>
                <div className={styles.inputRow}>
                  <input
                    id={`${menuId}-modify-price`}
                    className={styles.input}
                    inputMode="decimal"
                    value={modifyPriceInput}
                    onChange={(ev) => setModifyPriceInput(ev.target.value)}
                    autoComplete="off"
                  />
                </div>
              </>
            ) : null}

            {operation === 'cancelOwner' ? (
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={ownerCancelAnswer}
                  onChange={(ev) => setOwnerCancelAnswer(ev.target.checked)}
                />
                Send answer: true when checked (default is false)
              </label>
            ) : null}

            {operationNeedsCreateBody(operation) && !checkoutStepOpen && !hideAssetsBrowseCreateForm ? (
              <div className={styles.bodyFormPanel}>
                {createAssetId && assetPricingStatus === 'loading' ? (
                  <p className={styles.bookingOptionsStatus} role="status">
                    {t('bookings.loadingOptions')}
                  </p>
                ) : null}
                {createAssetId && !isEditingBooking && assetPricingStatus === 'ready' && assetPricingOptions.length > 0 ? (
                  <fieldset
                    className={styles.bookingPeriodFieldset}
                    aria-labelledby={`${menuId}-booking-period-legend`}
                  >
                    <legend className={styles.bookingPeriodLegend} id={`${menuId}-booking-period-legend`}>
                      {t('bookings.pricing')}
                    </legend>
                    <div
                      className={styles.bookingPeriodOptions}
                      role="radiogroup"
                      aria-label={t('bookings.pricing')}
                    >
                      {assetPricingOptions.map((opt) => {
                        const priceLabel =
                          opt.price !== null
                            ? formatMoneyAmount(opt.price, bookingCurrency)
                            : null;
                        const inputId = `${menuId}-period-${opt.pricingTypeId}`;
                        const checked = selectedPricingTypeId === opt.pricingTypeId;
                        return (
                          <label
                            key={opt.pricingTypeId}
                            className={`${styles.bookingPeriodOption}${checked ? ` ${styles.bookingPeriodOptionChecked}` : ''}`}
                            htmlFor={inputId}
                          >
                            <input
                              id={inputId}
                              type="radio"
                              name={`${menuId}-booking-period`}
                              className={styles.bookingPeriodRadio}
                              checked={checked}
                              onChange={() => {
                                setSelectedPricingTypeId(opt.pricingTypeId);
                                setPeriodScheduleWarning(null);
                              }}
                            />
                            <span className={styles.bookingPeriodOptionBody}>
                              <span className={styles.bookingPeriodOptionName}>
                                {localizeCatalogDisplayName(opt.label, language, 'pricing')}
                              </span>
                              {priceLabel ? (
                                <span className={styles.bookingPeriodOptionPrice}>{priceLabel}</span>
                              ) : null}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {usePeriodBasedScheduling &&
                    selectedBookingOption?.kind &&
                    selectedBookingOption.kind !== 'hour' ? (
                      <label className={styles.bookingPeriodCountField} htmlFor={`${menuId}-period-count`}>
                        <span className={styles.bookingPeriodCountLabel}>{t('bookings.howMany')}</span>
                        <input
                          id={`${menuId}-period-count`}
                          className={styles.bookingPeriodCountInput}
                          type="number"
                          min={1}
                          step={1}
                          inputMode="numeric"
                          value={bookingPeriodCount}
                          onChange={(ev) => {
                            const n = Number(ev.target.value);
                            setPeriodScheduleWarning(null);
                            setBookingPeriodCount(Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1);
                          }}
                        />
                        <span className={styles.bookingPeriodCountUnit}>
                          {formatBookingPeriodCountLabel(
                            t,
                            selectedBookingOption.kind,
                            bookingPeriodCount,
                          )}
                        </span>
                      </label>
                    ) : null}
                  </fieldset>
                ) : null}
                {isEditingBooking && usePeriodBasedScheduling && selectedBookingOption?.kind ? (
                  <fieldset
                    className={styles.bookingPeriodFieldset}
                    aria-labelledby={`${menuId}-edit-period-legend`}
                  >
                    <legend className={styles.bookingPeriodLegend} id={`${menuId}-edit-period-legend`}>
                      {t('bookings.extendLegend')}
                    </legend>
                    <p className={styles.addonHint} role="status">
                      {t('bookings.paymentOption')}{' '}
                      <strong>{localizedPricingLabel}</strong>
                    </p>
                    <label className={styles.bookingPeriodCountField} htmlFor={`${menuId}-edit-period-count`}>
                      <span className={styles.bookingPeriodCountLabel}>
                        {selectedBookingOption.kind === 'hour'
                          ? t('bookings.howManyHours')
                          : t('bookings.howMany')}
                      </span>
                      <input
                        id={`${menuId}-edit-period-count`}
                        className={styles.bookingPeriodCountInput}
                        type="number"
                        min={editMinPeriodCount}
                        max={
                          selectedBookingOption.kind === 'hour' && maxHourlyPeriodCount !== null
                            ? maxHourlyPeriodCount
                            : undefined
                        }
                        step={1}
                        inputMode="numeric"
                        value={bookingPeriodCount}
                        onChange={(ev) => {
                          const n = Number(ev.target.value);
                          const floor = editMinPeriodCount;
                          const ceiling =
                            selectedBookingOption.kind === 'hour' && maxHourlyPeriodCount !== null
                              ? maxHourlyPeriodCount
                              : Number.POSITIVE_INFINITY;
                          const next =
                            Number.isFinite(n) && n >= floor
                              ? Math.min(Math.floor(n), ceiling)
                              : floor;
                          setBookingPeriodCount(next);
                        }}
                      />
                      <span className={styles.bookingPeriodCountUnit}>
                        {formatBookingPeriodCountLabel(
                          t,
                          selectedBookingOption.kind,
                          bookingPeriodCount,
                        )}
                      </span>
                    </label>
                  </fieldset>
                ) : null}
                {showBookingCalendar ? (
                  <AssetBookingCalendar
                    reservedDayKeys={assetReservedDayKeys}
                    isDayClosed={(dayKey) =>
                      createBookingAsset ? calendarDayIsClosed(createBookingAsset, dayKey) : false
                    }
                    startDayKey={bookingStartDayKey}
                    endDayKey={bookingEndDayKey}
                    onStartDaySelect={onCalendarStartDaySelect}
                    onEndDaySelect={onCalendarEndDaySelect}
                    language={language}
                    theme={isBrowsePage ? 'light' : 'dark'}
                    loading={assetBookingsCalendarStatus === 'loading'}
                    labels={{
                      title: isEditingBooking
                        ? t('bookings.calendar.titleExtend')
                        : isHourlySameDayBooking
                          ? t('bookings.calendar.titleHourly')
                          : t('bookings.calendar.title'),
                      hint: isEditingBooking
                        ? isHourlySameDayBooking
                          ? t('bookings.calendar.hintHourlyExtend')
                          : t('bookings.calendar.hintExtend')
                        : isHourlySameDayBooking
                          ? t('bookings.calendar.hintHourly')
                          : t('bookings.calendar.hint'),
                      startDate: t('bookings.calendar.startDate'),
                      endDate: t('bookings.calendar.endDate'),
                      selectedDay: t('bookings.calendar.selectedDay'),
                      notSelected: t('bookings.calendar.notSelected'),
                      pickStart: t('bookings.calendar.pickStart'),
                      pickEnd: t('bookings.calendar.pickEnd'),
                      pickEndNeedsStart: t('bookings.calendar.pickEndNeedsStart'),
                      reservedLegend: t('bookings.calendar.reservedLegend'),
                      closedLegend: t('bookings.calendar.closedLegend'),
                      rangeLegend: t('bookings.calendar.rangeLegend'),
                      prevMonth: t('bookings.calendar.prevMonth'),
                      nextMonth: t('bookings.calendar.nextMonth'),
                      loading: t('bookings.calendar.loading'),
                      rangeBlocked: t('bookings.calendar.rangeBlocked'),
                      startTime: t('bookings.calendar.startTime'),
                      endTime: t('bookings.calendar.endTime'),
                      hourlyHint: isHourlySameDayBooking
                        ? t('bookings.calendar.hourlySameDayHint')
                        : t('bookings.calendar.hourlyHint'),
                      endTimeAuto: t('bookings.calendar.endTimeAuto'),
                    }}
                    hourly={showBookingTimeInputs}
                    startTime={calendarStartTime}
                    endTime={calendarEndTime}
                    onStartTimeChange={onCalendarStartTimeChange}
                    onEndTimeChange={onCalendarEndTimeChange}
                    showEndTimePicker={hourlyCalendarShowEndTimePicker}
                    idPrefix={`${menuId}-cal`}
                    lockStartPick={isEditingBooking}
                    singleDayPick={isHourlySameDayBooking}
                    disableDayPick={isHourlySameDayBooking && isEditingBooking}
                  />
                ) : null}
                {isHourlySameDayBooking && !isEditingBooking ? (
                  <label className={styles.bookingPeriodCountField} htmlFor={`${menuId}-hour-count`}>
                    <span className={styles.bookingPeriodCountLabel}>{t('bookings.howManyHours')}</span>
                    <input
                      id={`${menuId}-hour-count`}
                      className={styles.bookingPeriodCountInput}
                      type="number"
                      min={1}
                      max={maxHourlyPeriodCount ?? undefined}
                      step={1}
                      inputMode="numeric"
                      value={bookingPeriodCount}
                      disabled={!bookingStartDayKey || maxHourlyPeriodCount === null}
                      onChange={(ev) => {
                        const n = Number(ev.target.value);
                        const ceiling = maxHourlyPeriodCount ?? Number.POSITIVE_INFINITY;
                        const next =
                          Number.isFinite(n) && n >= 1
                            ? Math.min(Math.floor(n), ceiling)
                            : 1;
                        setBookingPeriodCount(next);
                      }}
                    />
                    <span className={styles.bookingPeriodCountUnit}>
                      {formatBookingPeriodCountLabel(t, 'hour', bookingPeriodCount)}
                    </span>
                  </label>
                ) : null}
                {hideLegacyScheduleDatePickers &&
                usePeriodBasedScheduling &&
                !isHourlySameDayBooking ? (
                  <div className={styles.computedEndField} style={{ marginTop: '0.5rem' }}>
                    <span className={styles.scheduleFieldLabel}>{t('bookings.endCalculated')}</span>
                    <p className={styles.computedEndValue}>
                      {formatBookingInstantPreview(createBody.end_date, showBookingTimeInputs)}
                    </p>
                    {selectedBookingOption?.kind ? (
                      <p className={styles.computedEndHint}>
                        {formatBookingPeriodStartPlus(
                          t,
                          selectedBookingOption.kind,
                          bookingPeriodCount,
                        )}
                      </p>
                    ) : null}
                    {periodScheduleWarning ? (
                      <p className={styles.periodScheduleWarning} role="status">
                        {periodScheduleWarning}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {!hideLegacyScheduleDatePickers ? (
                  <>
                    <label className={styles.label} id={`${menuId}-schedule-label`} style={{ marginTop: '0.5rem' }}>
                      {isEditingBooking
                        ? t('bookings.schedule')
                        : showBookingTimeInputs
                          ? t('bookings.startDateTime')
                          : t('bookings.startDate')}
                    </label>
                    <div className={styles.scheduleGrid} aria-labelledby={`${menuId}-schedule-label`}>
                      {isEditingBooking ? (
                        <div className={styles.computedEndField}>
                          <span className={styles.scheduleFieldLabel}>{t('bookings.startFixed')}</span>
                          <p className={styles.computedEndValue}>
                            {formatBookingInstantPreview(createBody.start_date, showBookingTimeInputs)}
                          </p>
                        </div>
                      ) : (
                        <BookingScheduleBlock
                          idPrefix={`${menuId}-start`}
                          legend={t('bookings.startLegend')}
                          display={startSchedule.display}
                          setPart={startSchedule.setPart}
                          inputClass={styles.scheduleInputCompact}
                          monthSelectClass={styles.scheduleMonthSelect}
                          controlsClass={styles.scheduleControls}
                          fieldClass={styles.scheduleField}
                          fieldLabelClass={styles.scheduleFieldLabel}
                          showTime={showBookingTimeInputs}
                        />
                      )}
                      {usePeriodBasedScheduling ? (
                        <div className={styles.computedEndField}>
                          <span className={styles.scheduleFieldLabel}>{t('bookings.endCalculated')}</span>
                          <p className={styles.computedEndValue}>
                            {formatBookingInstantPreview(createBody.end_date, showBookingTimeInputs)}
                          </p>
                          {selectedBookingOption?.kind ? (
                            <p className={styles.computedEndHint}>
                              {formatBookingPeriodStartPlus(
                                t,
                                selectedBookingOption.kind,
                                bookingPeriodCount,
                              )}
                            </p>
                          ) : null}
                          {periodScheduleWarning ? (
                            <p className={styles.periodScheduleWarning} role="status">
                              {periodScheduleWarning}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <BookingScheduleBlock
                          idPrefix={`${menuId}-end`}
                          legend={t('bookings.endLegend')}
                          display={endSchedule.display}
                          setPart={endSchedule.setPart}
                          inputClass={styles.scheduleInputCompact}
                          monthSelectClass={styles.scheduleMonthSelect}
                          controlsClass={styles.scheduleControls}
                          fieldClass={styles.scheduleField}
                          fieldLabelClass={styles.scheduleFieldLabel}
                          showTime={showBookingTimeInputs}
                        />
                      )}
                    </div>
                  </>
                ) : null}
                <p className={styles.bookingCalculatedTotal} role="status">
                  <span className={styles.label}>{t('bookings.totalPriceCalculated')}</span>
                  <span className={styles.computedEndValue}>
                    {computedBookingTotalPrice !== null
                      ? formatMoneyAmount(computedBookingTotalPrice, bookingCurrency)
                      : createAssetId && assetPricingStatus === 'loading'
                        ? t('bookings.loadingAssetRates')
                        : '—'}
                  </span>
                  {selectedBookingOption?.label && computedBookingTotalPrice !== null ? (
                    <span className={styles.bookingTotalPriceHint}>
                      {selectedBookingOption.price !== null
                        ? t('bookings.totalPriceBasedOn', {
                            pricing: localizedPricingLabel,
                            price: formatMoneyAmount(selectedBookingOption.price, bookingCurrency),
                          })
                        : t('bookings.totalPriceBasedOnNoUnit', {
                            pricing: localizedPricingLabel,
                          })}
                    </span>
                  ) : null}
                  {computedBookingTotalPrice === null && assetPricingStatus === 'ready' ? (
                    <span className={styles.bookingTotalPriceHint}>
                      {t('bookings.totalPriceHintEnd')}
                    </span>
                  ) : null}
                  {computedBookingTotalPrice !== null ? (
                    <span className={styles.bookingTotalPriceHint}>
                      {t('assetProfile.pricesExcludeVat')}
                    </span>
                  ) : null}
                </p>
                {!isEditingBooking && bookingAddonChoices.length > 0 ? (
                <fieldset className={styles.addonFieldset} aria-labelledby={`${menuId}-addons-label`}>
                  <legend className={styles.addonLegend} id={`${menuId}-addons-label`}>
                    {t('bookings.addonsLegend')}
                  </legend>
                  <div
                    className={styles.addonGrid}
                    role="group"
                    aria-label={t('bookings.optionalAddons')}
                  >
                    {bookingAddonChoices.map((opt) => {
                      const boxId = `${menuId}-addon-${opt.addonId}`;
                      const checked = selectedBookingAddonIds.has(opt.addonId);
                      const quantity = bookingAddonQuantities.get(opt.addonId) ?? 1;
                      const priceLabel =
                        opt.price !== null ? formatMoneyAmount(opt.price, bookingCurrency) : null;
                      const availabilityLabel = formatBookingAddonAvailabilityLabel(
                        opt,
                        editingBookingReservedAddonQty.get(opt.addonId) ?? 0,
                        isEditingBooking,
                        t,
                      );
                      const showQuantityInput =
                        checked && (opt.maxQuantity === null || opt.maxQuantity > 1);
                      return (
                        <div
                          key={opt.addonId}
                          className={`${styles.addonBox}${checked ? ` ${styles.addonBoxChecked}` : ''}`}
                        >
                          <label className={styles.addonBoxLabel} htmlFor={boxId}>
                            <input
                              id={boxId}
                              type="checkbox"
                              className={styles.addonBoxCheckbox}
                              checked={checked}
                              onChange={(ev) => onBookingAddonToggle(opt.addonId, ev.target.checked)}
                            />
                            <span className={styles.addonBoxBody}>
                              <span className={styles.addonBoxTitle}>{opt.label}</span>
                              {priceLabel ? (
                                <span className={styles.addonBoxHintInline}>{priceLabel}</span>
                              ) : null}
                              {availabilityLabel ? (
                                <span className={styles.addonBoxAvailability}>{availabilityLabel}</span>
                              ) : null}
                            </span>
                          </label>
                          {showQuantityInput ? (
                            <label className={styles.addonQtyRow} htmlFor={`${boxId}-qty`}>
                              <span className={styles.addonQtyLabel}>{t('common.qty')}</span>
                              <input
                                id={`${boxId}-qty`}
                                className={styles.addonQtyInput}
                                type="number"
                                min={1}
                                max={opt.maxQuantity ?? undefined}
                                step={1}
                                inputMode="numeric"
                                value={quantity}
                                onChange={(ev) => onBookingAddonQuantityChange(opt.addonId, ev.target.value)}
                              />
                            </label>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </fieldset>
                ) : null}
                {!isEditingBooking &&
                !(
                  createBookingAsset &&
                  assetIsParkingBooking(createBookingAsset)
                ) ? (
                  <label className={styles.bookingPeopleField} htmlFor={`${menuId}-amount-of-people`}>
                    <span className={styles.label}>{t('bookings.numberOfPeople')}</span>
                    <input
                      id={`${menuId}-amount-of-people`}
                      className={styles.bookingPeopleInput}
                      type="number"
                      min={1}
                      step={1}
                      inputMode="numeric"
                      value={
                        typeof createBody.amount_of_people === 'number' &&
                        Number.isFinite(createBody.amount_of_people)
                          ? String(Math.floor(createBody.amount_of_people))
                          : ''
                      }
                      onChange={(ev) => {
                        const raw = ev.target.value.trim();
                        if (raw === '') {
                          onCreateFieldChange(['amount_of_people'], null);
                          return;
                        }
                        const n = Number(raw);
                        onCreateFieldChange(
                          ['amount_of_people'],
                          Number.isFinite(n) && n >= 1 ? Math.floor(n) : null,
                        );
                      }}
                    />
                  </label>
                ) : null}
              </div>
            ) : null}

            {isBrowsePage && (operation === 'listAll' || hideAssetsBrowseCreateForm) ? null : (
            <div className={styles.inputRow} style={{ marginTop: '0.65rem', marginBottom: 0 }}>
              <button
                type="submit"
                className={operationUsesDangerSubmit(operation) ? styles.submitDanger : styles.submit}
                disabled={loading}
              >
                {submitLabel(t, operation, loading, editingBookingId)}
              </button>
            </div>
            )}
          </form>
          {statusNotice ? (
            <p
              ref={bookingStatusNoticeRef}
              tabIndex={-1}
              className={
                isBrowsePage ? pageThemeStyles.browseCreateSuccess : styles.bookingStatusNotice
              }
              role="status"
            >
              {statusNotice}
            </p>
          ) : null}
          {error && !showBrowseAssetCreateError ? (
            <div ref={bookingErrorRef} tabIndex={-1} className={styles.bookingErrorBlock} role="alert">
              <p className={styles.bookingErrorPrimary}>
                {formatBookingUserError(error)}
              </p>
            </div>
          ) : null}
          {createConfirmation ? (
            <BookingConfirmationCard
              details={createConfirmation}
              onClose={() => setCreateConfirmation(null)}
              closeLabel={t('bookings.confirmation.close')}
            />
          ) : null}
          {awaitingPaymentBookingId && !createConfirmation ? (
            <BookingAwaitingPaymentPanel
              bookingId={awaitingPaymentBookingId}
              processing={awaitingPaymentBusy}
              onPayNow={() => void onResumePayplusForAwaitingBooking()}
              onClose={() => setAwaitingPaymentBookingId(null)}
            />
          ) : null}
          {bookingsBrowseParams.paymentReturn ? (
            <BookingPaymentReturnPanel
              outcome={bookingsBrowseParams.paymentReturn}
              bookingId={bookingsBrowseParams.bookingId}
              onDone={({ bookingId: paidId, confirmed }) => {
                const pending = readPayplusPendingCheckout();
                const returnAssetId =
                  bookingsBrowseParams.returnAssetId?.trim() ||
                  pending?.returnAssetId?.trim() ||
                  null;
                clearPayplusPendingCheckout();
                clearBookingsBrowseBookingId();

                // Do not call onSubmit here — it clears createConfirmation and races the QR card.
                if (confirmed && paidId) {
                  openBookings();
                  void (async () => {
                    await showConfirmedBookingCard(paidId);
                    try {
                      await refreshBookingsList();
                    } catch {
                      /* list refresh is best-effort after QR is shown */
                    }
                  })();
                  return;
                }

                // Cancel/failure: match Bit/credits cancel — return to the asset when we have one.
                if (returnAssetId) {
                  openLastBookedAssetDetail(returnAssetId, { replace: true });
                  return;
                }

                if (paidId) {
                  setAwaitingPaymentBookingId(paidId);
                  setCreateConfirmation(null);
                  openBookings({ bookingId: paidId });
                  void refreshBookingsList().catch(() => {});
                  return;
                }
                openBookings();
                void refreshBookingsList().catch(() => {});
              }}
            />
          ) : null}
          {parkingVehicleOpen ? (
            <BookingParkingVehiclePanel
              initialVehicleId={parkingVehicleIdRef.current}
              onContinue={onParkingVehicleChosen}
              onCancel={onPaymentCancelled}
            />
          ) : null}
          {healthDeclarationOpen ? (
            <GymHealthDeclarationPanel
              onSubmit={onHealthDeclarationSubmitted}
              onCancel={onPaymentCancelled}
            />
          ) : null}
          {cancellationPolicyOpen ? (
            <BookingCancellationPolicyPanel
              onContinue={onCancellationPolicyAccepted}
              onCancel={onCancellationPolicyCancelled}
            />
          ) : null}
          {paymentOpen && paymentCheckoutView === 'worker-credits' ? (
            <BookingWorkerCreditsPaymentPanel
              amountLabel={paymentDisplayAmountLabel}
              processing={loading}
              onPayWithCredits={onWorkerCreditsConfirmed}
              onOpenRegularPayment={onOpenRegularPaymentFromCredits}
              onCancel={onPaymentCancelled}
            />
          ) : null}
          {paymentOpen && paymentCheckoutView === 'regular' ? (
            <BookingPaymentPanel
              amountLabel={paymentDisplayAmountLabel}
              processing={loading}
              onPay={onPaymentConfirmed}
              onPayWithWalletCredits={
                privateClientCheckout ? onPrivateClientWalletCreditsConfirmed : undefined
              }
              walletCreditsBalanceLabel={
                privateClientCheckout ? walletCreditsBalanceLabel : null
              }
              onCancel={onPaymentCancelled}
            />
          ) : null}
          {response && operation === 'listAll' ? (
            <>
              {response.operation === 'listAll' ? null : (
                <p className={isBrowsePage ? pageThemeStyles.bookingsSectionLabel : styles.resultLabel} id={`${menuId}-response-label`}>
                  Response
                </p>
              )}
              {response.operation === 'listAll' ? (
                <>
                  {showBookingListAssetPreview ? (
                    <div className={isBrowsePage ? pageThemeStyles.browseListWrap : undefined}>
                      <div className={isBrowsePage ? pageThemeStyles.browseToolbar : styles.createFlowToolbar}>
                        <button
                          type="button"
                          className={isBrowsePage ? pageThemeStyles.browseBackButton : styles.createFlowBackButton}
                          onClick={clearBookingAssetPreview}
                        >
                          {t('bookings.backToBookings')}
                        </button>
                      </div>
                      {bookingAssetPreviewLoading && !displayedBookingAssetPreview ? (
                        <p
                          className={isBrowsePage ? pageThemeStyles.browseEmpty : styles.emptyHint}
                          role="status"
                        >
                          {t('common.loading')}
                        </p>
                      ) : null}
                      {bookingAssetPreviewError && !displayedBookingAssetPreview ? (
                        <p
                          ref={bookingAssetPreviewErrorRef}
                          tabIndex={-1}
                          className={isBrowsePage ? pageThemeStyles.browseError : styles.error}
                          role="alert"
                        >
                          {bookingAssetPreviewError}
                        </p>
                      ) : null}
                      {displayedBookingAssetPreview ? (
                        <AssetGetByIdView
                          data={displayedBookingAssetPreview}
                          theme={isBrowsePage ? 'light' : 'dark'}
                          layout="profile"
                        />
                      ) : null}
                    </div>
                  ) : (
                    <>
                  <div
                    className={
                      isBrowsePage ? pageThemeStyles.bookingsTimeFilterBar : styles.bookingsTimeFilterBar
                    }
                    role="group"
                    aria-label={t('bookings.list.timeFilterLabel')}
                  >
                    <div
                      className={
                        isBrowsePage
                          ? pageThemeStyles.bookingsTimeFilterSwitch
                          : styles.bookingsTimeFilterSwitch
                      }
                    >
                      <button
                        type="button"
                        className={[
                          isBrowsePage
                            ? pageThemeStyles.bookingsTimeFilterBtn
                            : styles.bookingsTimeFilterBtn,
                          bookingsListTimeFilter === 'currentAndFuture'
                            ? isBrowsePage
                              ? pageThemeStyles.bookingsTimeFilterBtnActive
                              : styles.bookingsTimeFilterBtnActive
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        aria-pressed={bookingsListTimeFilter === 'currentAndFuture'}
                        onClick={() => setBookingsListTimeFilter('currentAndFuture')}
                      >
                        {t('bookings.list.timeFilterCurrent')}
                      </button>
                      <button
                        type="button"
                        className={[
                          isBrowsePage
                            ? pageThemeStyles.bookingsTimeFilterBtn
                            : styles.bookingsTimeFilterBtn,
                          bookingsListTimeFilter === 'past'
                            ? isBrowsePage
                              ? pageThemeStyles.bookingsTimeFilterBtnActive
                              : styles.bookingsTimeFilterBtnActive
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        aria-pressed={bookingsListTimeFilter === 'past'}
                        onClick={() => setBookingsListTimeFilter('past')}
                      >
                        {t('bookings.list.timeFilterPast')}
                      </button>
                    </div>
                  </div>
                  <div className={isBrowsePage ? pageThemeStyles.browseListWrap : undefined}>
                    <BookingResponseView
                      data={bookingsListResponseData}
                      responseLabelId={`${menuId}-response-label`}
                      listEmptyMessage={
                        bookingsListTimeFilter === 'past'
                          ? t('bookings.noPastBookingsListed')
                          : t('bookings.noUpcomingBookingsListed')
                      }
                      onUpdateBooking={onUpdateBookingFromList}
                      onCancelBooking={onCancelBookingFromList}
                      onViewAsset={onViewAssetFromList}
                      onViewConfirmation={onViewConfirmationFromList}
                      cancellingBookingId={cancellingBookingId}
                      activeBookingsOnly
                      listTimeFilter={bookingsListTimeFilter}
                      initialDetailBookingId={bookingsBrowseParams.bookingId}
                      onInitialDetailClosed={clearBookingsBrowseBookingId}
                      theme={pageTheme ? 'light' : 'dark'}
                    />
                  </div>
                    </>
                  )}
                </>
              ) : (
                <div className={isBrowsePage ? pageThemeStyles.browseListWrap : undefined}>
                  <BookingResponseView
                    data={response.data}
                    responseLabelId={`${menuId}-response-label`}
                    onUpdateBooking={undefined}
                    onCancelBooking={undefined}
                    onViewAsset={undefined}
                    onViewConfirmation={undefined}
                    cancellingBookingId={cancellingBookingId}
                    theme={pageTheme ? 'light' : 'dark'}
                  />
                </div>
              )}
            </>
          ) : !error && !loading && operationEmptyHint ? (
            <p className={isBrowsePage ? pageThemeStyles.browseEmpty : styles.emptyHint}>{operationEmptyHint}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
